import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, defaultIfEmpty, map, Observable, of, switchMap, throwError } from 'rxjs';
import { AlertComponent } from '../alert/alert.component';
import { MrfConfirmAlertComponent, MrfConfirmAlertData } from './mrf-confirm-alert/mrf-confirm-alert.component';
import {
  EMPTY_MRF_HRMS_PERMISSIONS,
  MrfHrmsPermissions,
  parseMrfHrmsPermissions,
  normalizeMrfGenderValue,
  MRF_AADHAAR_DIGIT_LENGTH,
  normalizeMrfAadhaarDigits,
  validateMrfAadhaarNo,
  resolveMrfRehiringAadharPreferringSaved,
  resolveMrfRehiringAadharNo,
  resolveMrfTempPayrollId,
  resolveMrfYearsOfExperience,
  getMrfUploadFileSizeValidationMessage,
} from './mrf-view-options';
import { environment } from 'src/environments/environment';

export interface MrfDocumentView {
  fileName: string;
  documentName?: string;
  documentType?: string;
  mimeType?: string;
  fileUrl?: string;
  blobUrl?: string;
  documentId?: string | number;
  mrfDocId?: string | number;
}

export const MRF_DOCUMENT_TYPE = {
  AADHAR: 'aadhar',
  PAN: 'pan',
  WORK_DESCRIPTION: 'work description',
  APPROVED_BY_MANAGEMENT: 'approvedbymanagement',
  EXAM_COPY: 'exam',
} as const;

export interface MrfDocumentBuckets {
  aadhaar: MrfDocumentView[];
  pan: MrfDocumentView[];
  exam: MrfDocumentView[];
  demoVideo: MrfDocumentView[];
  workDescription: MrfDocumentView[];
  approvedByManagement: MrfDocumentView[];
  attached: MrfDocumentView[];
}

export interface MrfApprovedByOption {
  employeeId: number | null;
  employeeName: string;
  payrollId: string;
  desigId: number | null;
  desigName: string;
  name: string;
  label: string;
}

export type HrAlertStatus = 'Warning' | 'Error' | 'Success';

const MRF_API = '/employee/api/jwtV2/mrf';
const COMMON_API = '/apiV2/common';
const JWT_COMMON_API = '/employee/api/jwtV2/common';
const EMPLOYEE_API = '/employee/api/jwtV2/employee';

export interface MrfLocationFilters {
  los?: string | null;
  stateId?: number | null;
  cityId?: number | null;
  zoneName?: string | null;
  subZone?: string | null;
  deanName?: string | null;
  campusId?: number | null;
  buildingId?: number | null;
}

export interface MrfTrackHistoryFilterPayload {
  stateId: number | null;
  zone: string | null;
  subZone: string | null;
  cityId: number | null;
  campusId: number | null;
  buildingId: number | null;
  month: number | null;
  year: number | null;
  location: string | null;
  deanName: string | null;
  duration?: string;
  status?: string;
}

export type MrfCustomizeFilterField =
  | 'los'
  | 'state'
  | 'city'
  | 'zone'
  | 'subZone'
  | 'deanName'
  | 'campus';

export const MRF_CUSTOMIZE_FILTER_BY_API: Record<MrfCustomizeFilterField, string> = {
  los: 'LOCATION',
  state: 'STATE',
  city: 'CITY',
  zone: 'ZONE',
  subZone: 'SUB_ZONE',
  deanName: 'DEAN',
  campus: 'CAMPUS',
};

export interface MrfCustomizeFilterPayload {
  filterBy: string;
  location: string | null;
  zone: string | null;
  subZone: string | null;
  stateId: number | null;
  cityId: number | null;
  campusId: number | null;
  deanName: string | null;
}

export interface SaveEmployeeAssessmentPayload {
  mrfFormId: number;
  employeeName?: string;
  surname?: string;
  mobileNo?: string;
  aadhaarNo?: string;
  panNo?: string;
  gender?: string;
  examTaken?: boolean;
  dob?: string;
  expectedSal?: string | number;
  tempPayrollId: string;
}

export interface SaveEmployeeWithdrawalPayload {
  payrollId: string;
  remarks: string;
}

export interface EmployeeWithdrawalView {
  payrollId: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  campusName: string;
  mobileNo: string;
  email: string;
  dateOfJoining: string;
  employeeType: string;
}

export interface SaveMrfFormPayload {
  mrfFormId: number | null;
  campusId: number;
  roleType: string;
  empType: string;
  deptId: number;
  desigId: number;
  subId: number;
  workLoad: number;
  positionType: string;
  hiringReason: string;
  reasonDescription: string;
  replacementEmpId: number;
  isReHiring: boolean;
  prevChaitanyaId: number | null;
  aadharNo: string;
  qualificationId: number;
  additionalQualification: string;
  yearsOfExp: string;
  skills: string;
  reportingManagerId: number;
  dob: string;
  rehiringPayrollId: string;
}

export interface ManpowerBudgetListItem {
  fusionCampusCode: string;
  /** Teaching-type id for saveCampusBudget (API field name). */
  teachTypeId: number;
  deptId: number;
  desigId: number;
  subjectId: number | null;
  budgetAmount: number;
  empCount: number;
  minSalary: number;
  maxSalary: number;
  avgSalary: number;
}

export interface SaveCampusBudgetPayload {
  /** Present when updating an existing campus budget from budget report edit. */
  campusBudgetId?: number;
  campusId: number;
  budgetYear: string;
  remarks: string;
  manpowerBudgetList: ManpowerBudgetListItem[];
}

export interface MrfAvailableBudgetView {
  budgetId: number | null;
  budgetAmount: number | null;
  totalUsedBudget: number | null;
  totalExpectedSal: number | null;
  availableBudget: number | null;
}

export interface BudgetReportRow {
  budgetId: number | null;
  city: string;
  zone: string;
  subZone: string;
  campus: string;
  campusId: number | null;
  deanName: string;
  empType: string;
  empTypeId: number | null;
  teachingType: string;
  teachTypeId: number | null;
  department: string;
  designation: string;
  desigId: number | null;
  subjectId: number | null;
  subjectName: string;
  budgetAmount: number | null;
  /** From getCampusBudgetReportWithFilters — false when employees are allotted to this designation. */
  editable: boolean | null;
  allottedEmpCount: number | null;
  raw: Record<string, unknown>;
}

export interface BudgetReportResult {
  rows: BudgetReportRow[];
  totalBudget: number | null;
}

export interface ManpowerBudgetDashboardDetail {
  sno: number;
  campusName: string;
  stateName: string;
  cityName: string;
  zone: string;
  subZone: string;
  deanName: string;
  department: string;
  designation: string;
  subjectName: string;
  empTypeName: string;
  teachingType: string;
  budgetedPositions: number;
  filledPositions: number;
  positionBalance: number;
  allocatedBudget: number;
  utilizedBudget: number;
  balanceBudget: number;
  budgetStatus: string;
}

export interface ManpowerBudgetDashboardFilters {
  location: string | null;
  stateId: number | null;
  cityId: number | null;
  zone: string | null;
  subZone: string | null;
  campusId: number | null;
  deanName: string | null;
  empTypeId: number | null;
  deptId: number | null;
  desigId: number | null;
}

export interface ManpowerBudgetDashboard {
  totalBudgetedPositions: number;
  filledPositions: number;
  positionBalance: number;
  totalBudgetAmount: number;
  utilizedBudget: number;
  balanceBudget: number;
  positionWithinLimit: number;
  positionExceeded: number;
  budgetWithinLimit: number;
  budgetExceeded: number;
  positionPercentage: number;
  budgetPercentage: number;
  details: ManpowerBudgetDashboardDetail[];
}

export interface MrfAadhaarVerifyView {
  alreadyExists: boolean;
  /** True when API rejects Aadhaar (e.g. status NOT_FOUND / not eligible). */
  blocked: boolean;
  message: string;
  employeeData?: Record<string, unknown>;
  /** Raw checkEmployeeByAadhaar tempPayrollId (empty means exam not attempted). */
  tempPayrollId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HrHttpService {
  private readonly baseUrl =  String(environment.masterDomain || '').replace(/\/+$/, '');;
  private readonly masterDomainUrl = String(environment.masterDomain || '').replace(/\/+$/, '');
  private mrfHrmsPermissions: MrfHrmsPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };

  constructor(private http: HttpClient) {}

  private authHeaders(): HttpHeaders {
    const raw = localStorage.getItem('token') ?? '';
    const authorization = raw
      ? raw.startsWith('Bearer ')
        ? raw
        : `Bearer ${raw}`
      : '';
    return new HttpHeaders({
      Authorization: authorization,
    });
  }

  /** Treat HTTP 204 as successful empty response for MRF/HR APIs. */
  catchMrfNoContent<T>(err: unknown, fallback: T): Observable<T> {
    const status = (err as HttpErrorResponse)?.status;
    if (status === 204) {
      return of(fallback);
    }
    return throwError(() => err);
  }

  /** Ensures MRF API calls emit once (204 / empty body) and map 204 errors to fallback. */
  wrapMrfLoaderApi<T>(request$: Observable<T>, fallback: T): Observable<T> {
    return request$.pipe(
      defaultIfEmpty(fallback),
      catchError((err) => this.catchMrfNoContent(err, fallback))
    );
  }

  private mrfHttp<T>(request$: Observable<T>): Observable<T | null> {
    return this.wrapMrfLoaderApi(request$, null as T);
  }

  getMrfBasicInfo(){
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/basicInfo`, {
      headers: this.authHeaders(),
    }));
  }

  getUserRoles(): Observable<unknown> {
    const payRollId = localStorage.getItem('userId');
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${JWT_COMMON_API}/userRoles?payrollId=${payRollId}`,
      { headers: this.authHeaders() }
    ));
  }

  setMrfHrmsPermissionsFromResponse(res: unknown): MrfHrmsPermissions {
    this.mrfHrmsPermissions = parseMrfHrmsPermissions(res);
    return this.mrfHrmsPermissions;
  }

  getMrfHrmsPermissions(): MrfHrmsPermissions {
    return this.mrfHrmsPermissions;
  }

  getMrfTeachTypes() {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/teachTypes`, {
      headers: this.authHeaders(),
    }));
  }

  /**
   * Teach-type master / MRF GET use `roleType` (e.g. "NON TEACH").
   * Normalize to both `roleType` and `TeachType` for UI compatibility.
   */
  parseMrfTeachTypesResponse(
    res: unknown
  ): Array<Record<string, unknown> & { roleType: string; TeachType: string; typeId: string | number | null }> {
    const list = (res as { data?: unknown })?.data ?? res;
    if (!Array.isArray(list)) {
      return [];
    }

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const label = this.getMrfTeachTypeLabel(row);
        const typeIdRaw = row['typeId'] ?? row['teachTypeId'] ?? row['roleTypeId'] ?? null;
        const typeId =
          typeIdRaw != null && typeIdRaw !== '' && String(typeIdRaw) !== '0'
            ? (typeIdRaw as string | number)
            : null;
        return {
          ...row,
          roleType: label,
          TeachType: label,
          typeId,
        };
      })
      .filter((item) => !!item.roleType);
  }

  /** Label for a teach-type row or MRF payload (`roleType` is the API key). */
  getMrfTeachTypeLabel(data: Record<string, unknown> | null | undefined): string {
    if (!data) {
      return '';
    }
    for (const key of ['roleType', 'TeachType', 'teachType', 'teachingType']) {
      const value = data[key];
      if (value != null && String(value).trim() !== '' && String(value) !== '0') {
        return String(value).trim();
      }
    }
    return '';
  }

  getMrfTypeWiseDesigAndDepartments(typeId: string | number) {
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/typeWiseDesig&Departments?typeId=${encodeURIComponent(String(typeId))}`,
      { headers: this.authHeaders() }
    ));
  }

  getMrfCostCenterDetails() {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/costCenterDetails`, {
      headers: this.authHeaders(),
    }));
  }


  getMrfHeadCountInfo(params: {
    campusId: string | number;
    typeId?: string | number | null;
    deptId?: string | number | null;
    desigId?: string | number | null;
    subId?: string | number | null;
  }) {
    let url = `${this.baseUrl}${MRF_API}/headCountInfo?campusId=${params.campusId}`;
    if (params.typeId != null) url += `&typeId=${params.typeId}`;
    if (params.deptId != null) url += `&deptId=${params.deptId}`;
    if (params.desigId != null) url += `&desigId=${params.desigId}`;
    if (params.subId != null) url += `&subId=${params.subId}`;
    return this.mrfHttp(this.http.get(url, {
      headers: this.authHeaders().set('Accept', '*/*'),
    }));
  }

  /** Resolves teaching-type id from MRF data (typeId / teachTypeId / teachingTypeId). */
  resolveMrfTeachingTypeId(data: Record<string, unknown>): string | number | null {
    const pick = (value: unknown): string | number | null => {
      if (value == null || value === '' || value === '0') return null;
      return typeof value === 'string' || typeof value === 'number' ? value : null;
    };
    return (
      pick(data['typeId']) ??
      pick(data['teachTypeId']) ??
      pick(data['teachingTypeId']) ??
      pick(data['TeachTypeId'])
    );
  }

  /** Resolves teaching type id from roleType label using teachTypes master list. */
  matchMrfTeachingTypeIdFromRoleType(
    roleType: string,
    teachTypes: Array<Record<string, unknown> | { TeachType?: string; roleType?: string; typeId?: string | number | null }>
  ): string | number | null {
    const norm = this.normalizeMrfLookupToken(roleType);
    if (!norm || !Array.isArray(teachTypes)) return null;

    // Exact match only — "NON TEACH" must not match "TEACH" via includes().
    const match = teachTypes.find((item) => {
      const label = this.normalizeMrfLookupToken(
        this.getMrfTeachTypeLabel(item as Record<string, unknown>)
      );
      return label === norm;
    }) as { typeId?: string | number | null } | undefined;
    const typeId = match?.typeId;
    return typeId != null && typeId !== '' && String(typeId) !== '0' ? typeId : null;
  }

  /** Loads headCountInfo with teaching type sent as typeId (lookup by roleType when id is missing). */
  getMrfHeadCountInfoForMrfData(
    data: Record<string, unknown>,
    overrides?: {
      campusId?: string | number | null;
      typeId?: string | number | null;
      deptId?: string | number | null;
      desigId?: string | number | null;
      subId?: string | number | null;
    }
  ): Observable<unknown> {
    const pickId = (value: unknown): string | number | null => {
      if (value == null || value === '' || value === '0') return null;
      return typeof value === 'string' || typeof value === 'number' ? value : null;
    };

    const campusId =
      pickId(overrides?.campusId) ??
      pickId(data['campusId']) ??
      pickId(data['CampusId']);
    if (campusId == null) {
      return of(null);
    }

    const deptId =
      pickId(overrides?.deptId) ??
      pickId(data['deptId']) ??
      pickId(data['departmentId']);
    const desigId =
      pickId(overrides?.desigId) ??
      pickId(data['desigId']) ??
      pickId(data['designationId']);
    const roleType = this.getMrfTeachTypeLabel(data);
    const nonTeachRole = String(roleType ?? '')
      .trim()
      .toUpperCase()
      .replace(/[_-]+/g, ' ');
    const isNonTeach =
      nonTeachRole === 'NON TEACH' || nonTeachRole.includes('NON TEACH');
    const subId = isNonTeach
      ? null
      : pickId(overrides?.subId) ??
        pickId(data['subId']) ??
        pickId(data['subjectId']);
    const directTypeId =
      pickId(overrides?.typeId) ?? this.resolveMrfTeachingTypeId(data);

    const callHeadCount = (typeId: string | number | null | undefined) =>
      this.getMrfHeadCountInfo({ campusId, typeId, deptId, desigId, subId });

    if (directTypeId != null) {
      return callHeadCount(directTypeId);
    }

    if (!roleType) {
      return callHeadCount(null);
    }

    return this.getMrfTeachTypes().pipe(
      map((res) => this.parseMrfTeachTypesResponse(res)),
      switchMap((teachTypes) => {
        const typeId = this.matchMrfTeachingTypeIdFromRoleType(roleType, teachTypes);
        return callHeadCount(typeId);
      }),
      catchError((err) => {
        console.error('Failed to resolve teaching type for headCountInfo', err);
        return callHeadCount(null);
      })
    );
  }

  getMrfAvailableBudget(params: {
    campusId: string | number;
    desigId: string | number;
    subjectId?: string | number | null;
  }): Observable<unknown> {
    let url =
      `${this.baseUrl}${MRF_API}/getAvailableBudget?campusId=${encodeURIComponent(String(params.campusId))}` +
      `&desigId=${encodeURIComponent(String(params.desigId))}`;
    if (params.subjectId != null && params.subjectId !== '' && String(params.subjectId) !== '0') {
      url += `&subjectId=${encodeURIComponent(String(params.subjectId))}`;
    }
    return this.mrfHttp(this.http.get(url, {
      headers: this.authHeaders().set('Accept', '*/*'),
    }));
  }

  getMrfAvailableBudgetForMrfData(data: Record<string, unknown>): Observable<MrfAvailableBudgetView | null> {
    const pickId = (value: unknown): string | number | null => {
      if (value == null || value === '' || value === '0') return null;
      return typeof value === 'string' || typeof value === 'number' ? value : null;
    };

    const campusId = pickId(data['campusId']) ?? pickId(data['CampusId']);
    const desigId =
      pickId(data['desigId']) ??
      pickId(data['designationId']) ??
      pickId(data['DesignationId']);
    const subjectId =
      pickId(data['subId']) ??
      pickId(data['subjectId']) ??
      pickId(data['SubId']) ??
      pickId(data['SubjectId']);

    if (campusId == null || desigId == null) {
      return of(null);
    }

    return this.getMrfAvailableBudget({ campusId, desigId, subjectId }).pipe(
      map((res) => this.parseMrfAvailableBudgetResponse(res)),
      catchError((err) => {
        console.error('getAvailableBudget failed', err);
        return of(null);
      })
    );
  }

  parseMrfAvailableBudgetResponse(res: unknown): MrfAvailableBudgetView | null {
    if (res == null) return null;
    const raw =
      typeof res === 'object' && !Array.isArray(res)
        ? ((res as Record<string, unknown>)['data'] ??
          (res as Record<string, unknown>)['result'] ??
          res)
        : res;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return null;
    }
    const row = raw as Record<string, unknown>;
    const pickNum = (value: unknown): number | null => {
      if (value == null || value === '') return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    return {
      budgetId: pickNum(row['budgetId']),
      budgetAmount: pickNum(row['budgetAmount']),
      totalUsedBudget: pickNum(row['totalUsedBudget']),
      totalExpectedSal: pickNum(row['totalExpectedSal']),
      availableBudget: pickNum(row['availableBudget']),
    };
  }

  formatMrfBudgetAmount(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }
    return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  resolveMrfExpectedSalaryValue(
    data: Record<string, unknown>,
    controlValue?: unknown
  ): number | null {
    const controlRaw = String(controlValue ?? '').trim().replace(/,/g, '');
    if (controlRaw) {
      const parsed = Number(controlRaw);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim().replace(/,/g, '');
        }
      }
      return '';
    };

    const raw = pickString(
      data['expectedSalary'],
      data['expectedSal'],
      data['expectedCTC'],
      data['salaryBudget'],
      data['salary'],
      data['salaryBudgetPosition']
    );
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  isMrfExpectedSalaryOverAvailableBudget(
    expectedSalary: number | null | undefined,
    availableBudget: number | null | undefined
  ): boolean {
    if (expectedSalary == null || availableBudget == null) {
      return false;
    }
    return expectedSalary > availableBudget;
  }

  /** Previous replacement employee CTC (API: prevRepEmpCtc / currentCTC). */
  resolveMrfPrevRepEmpCtc(data: Record<string, unknown> | null | undefined): number | null {
    if (!data) return null;
    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim().replace(/,/g, '');
        }
      }
      return '';
    };
    const raw = pickString(
      data['prevRepEmpCtc'],
      data['prevRepEmpCTC'],
      data['prevRepEmpCtcAmount'],
      data['previousReplacementCtc'],
      data['previousEmpCtc'],
      data['prevEmpCtc'],
      data['currentCTC'],
      data['ctc'],
      data['currentSalary']
    );
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Percentage difference of expected salary vs previous replacement CTC.
   * ((expected - prevCtc) / prevCtc) * 100
   */
  calculateMrfReplacementSalaryPercentageDiff(
    expectedSalary: number | null | undefined,
    prevRepEmpCtc: number | null | undefined
  ): number | null {
    if (
      expectedSalary == null ||
      prevRepEmpCtc == null ||
      !Number.isFinite(expectedSalary) ||
      !Number.isFinite(prevRepEmpCtc) ||
      prevRepEmpCtc === 0
    ) {
      return null;
    }
    return ((expectedSalary - prevRepEmpCtc) / prevRepEmpCtc) * 100;
  }

  /** prevRepEmpCtc + percentage (e.g. 18% → prevCtc * 1.18). */
  calculateMrfPrevRepEmpCtcPlusPercentage(
    prevRepEmpCtc: number | null | undefined,
    percentage = 18
  ): number | null {
    if (prevRepEmpCtc == null || !Number.isFinite(prevRepEmpCtc)) {
      return null;
    }
    return prevRepEmpCtc + (prevRepEmpCtc * percentage) / 100;
  }

  formatMrfPercentageDiff(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
      return '—';
    }
    const rounded = Math.round(value * 100) / 100;
    const sign = rounded > 0 ? '+' : '';
    return `${sign}${rounded.toFixed(2)}%`;
  }

  private normalizeMrfLookupToken(value: string): string {
    return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
  }

  /** Parses GET .../headCountInfo response for budgeted and available headcount. */
  parseMrfHeadCountResponse(
    res: unknown,
    requestedHeadcount = 1
  ): { budgetedPositions: number; availableHeadcount: number; totalEmpCount: number } {
    const raw = this.extractHeadCountRaw(res);
    const totalEmp = this.pickHeadCountNumber(
      raw['totalEmpCount'],
      raw['budgetedPositions'],
      raw['noOfBudgetedPositions'],
      raw['budgeted']
    );
    const requested = Math.max(0, Number(requestedHeadcount) || 0);
    return {
      budgetedPositions: totalEmp,
      totalEmpCount: totalEmp,
      availableHeadcount: Math.max(0, totalEmp - requested),
    };
  }

  private extractHeadCountRaw(res: unknown): Record<string, unknown> {
    if (!res || typeof res !== 'object') return {};
    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
    if (Array.isArray(data)) {
      return (data[0] as Record<string, unknown>) ?? {};
    }
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  }

  private pickHeadCountNumber(...vals: unknown[]): number {
    for (const val of vals) {
      if (val != null && String(val).trim() !== '' && !Number.isNaN(Number(val))) {
        return Number(val);
      }
    }
    return 0;
  }

  getMrfReplacement(payrollId: string) {
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/replacement?payrollId=${encodeURIComponent(payrollId)}`,
      { headers: this.authHeaders() }
    ));
  }

  getMrfReplacementByEmployeeId(employeeId: string) {
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/replacement?employeeId=${encodeURIComponent(employeeId)}`,
      { headers: this.authHeaders() }
    ));
  }

  getMrfReplacementByLookupId(lookupId: string): Observable<unknown> {
    const id = String(lookupId ?? '').trim();
    if (!id) {
      return of(null);
    }
    return this.getMrfReplacementByEmployeeId(id).pipe(
      catchError(() => this.getMrfReplacement(id))
    );
  }

  /** GET .../LeftEmpSearch — search left employees by payroll/name and/or optional aadharNo. */
  searchLeftEmployees(
    searchCriteria = '',
    options?: { aadharNo?: string }
  ): Observable<unknown> {
    const criteria = String(searchCriteria ?? '').trim();
    const aadharNo = normalizeMrfAadhaarDigits(options?.aadharNo ?? '');
    if (!criteria && !aadharNo) {
      return of([]);
    }

    let params = new HttpParams();
    if (criteria) {
      params = params.set('searchCriteria', criteria);
    }
    if (aadharNo) {
      params = params.set('aadharNo', aadharNo);
    }

    return this.mrfHttp(
      this.http.get(`${this.baseUrl}${MRF_API}/LeftEmpSearch`, {
        headers: this.authHeaders().set('Accept', '*/*'),
        params,
      })
    );
  }

  /** GET .../currentAndLeftEmpSearch?searchCriteria=&campusId= — search current and left employees. */
  searchCurrentAndLeftEmployees(
    searchCriteria: string,
    campusId?: number | string | null
  ): Observable<unknown> {
    const criteria = String(searchCriteria ?? '').trim();
    if (!criteria) {
      return of([]);
    }
    let url =
      `${this.baseUrl}${MRF_API}/currentAndLeftEmpSearch` +
      `?searchCriteria=${encodeURIComponent(criteria)}`;
    const campus = campusId != null ? String(campusId).trim() : '';
    if (campus) {
      url += `&campusId=${encodeURIComponent(campus)}`;
    }
    return this.mrfHttp(this.http.get(url, {
      headers: this.authHeaders().set('Accept', '*/*'),
    }));
  }

  /** GET .../tempEmpSearch?searchCriteria= — search temporary employees. */
  searchTempEmployees(searchCriteria: string): Observable<unknown> {
    const criteria = String(searchCriteria ?? '').trim();
    if (!criteria) {
      return of([]);
    }
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/tempEmpSearch?searchCriteria=${encodeURIComponent(criteria)}`,
      { headers: this.authHeaders().set('Accept', '*/*') }
    ));
  }

  /** GET .../searchByEmployees?searchCriteria= — search employees by payroll or name. */
  searchByEmployees(searchCriteria: string): Observable<unknown> {
    const criteria = String(searchCriteria ?? '').trim();
    if (!criteria) {
      return of([]);
    }
    return this.http.get(
      `${this.baseUrl}${EMPLOYEE_API}/searchByEmployees?searchCriteria=${encodeURIComponent(criteria)}`,
      { headers: this.authHeaders().set('Accept', '*/*') }
    );
  }

  normalizeSearchByEmployeesResults(res: unknown): Record<string, unknown>[] {
    if (res == null) {
      return [];
    }
    const rawList = this.extractLeftEmpArray(res);
    return rawList.map((item) => {
      const record = this.unwrapLeftEmpRecord(item);
      const payrollId = this.pickLeftEmpText(record, 'payroll', 'payrollId', 'payRollId');
      const empName = this.pickLeftEmpText(record, 'name', 'empName', 'employeeName', 'userName');
      const empId = this.pickLeftEmpText(record, 'empId', 'employeeId');
      return {
        ...record,
        payroll: payrollId || record['payroll'],
        payrollId,
        payRollId: payrollId,
        name: empName,
        empName,
        employeeName: empName,
        userName: empName,
        empId: empId || record['empId'],
        employeeId: empId,
        mobileNo: this.pickLeftEmpText(record, 'mobileNo', 'mobileNumber', 'mobile', 'phoneNo'),
      };
    });
  }

  normalizeTempEmpSearchResults(res: unknown): Record<string, unknown>[] {
    const rawList = this.extractLeftEmpArray(res);
    return rawList.map((item) => this.normalizeTempEmpRecord(this.unwrapLeftEmpRecord(item)));
  }

  getTempEmpLookupId(item: Record<string, unknown>): string {
    return this.pickLeftEmpText(
      item,
      'tempPayrollId',
      'employeeTempId',
      'tempEmpId',
      'empTempId',
      'tempEmployeeId',
      'tempId',
      'employeeTempID',
      'payrollId',
      'payRollId',
      'employeeId',
      'empId',
      'uniqueId',
      'id'
    );
  }

  findTempEmpSearchMatch(
    list: Record<string, unknown>[],
    lookupId: string
  ): Record<string, unknown> | null {
    const id = String(lookupId ?? '').trim().toLowerCase();
    if (!id || !list.length) {
      return null;
    }

    const matchesId = (emp: Record<string, unknown>): boolean => {
      const candidates = [
        this.getTempEmpLookupId(emp),
        emp['employeeTempId'],
        emp['tempEmpId'],
        emp['empTempId'],
        emp['tempEmployeeId'],
        emp['tempId'],
        emp['payrollId'],
        emp['payRollId'],
        emp['employeeId'],
        emp['empId'],
        emp['uniqueId'],
        emp['id'],
      ]
        .map((val) => String(val ?? '').trim().toLowerCase())
        .filter(Boolean);
      return candidates.includes(id);
    };

    return list.find(matchesId) ?? list[0] ?? null;
  }

  /** Maps tempEmpSearch row to Section 6 new-employee fields. */
  mapTempEmpToViewData(item: Record<string, unknown>): Record<string, unknown> {
    const normalized = this.normalizeTempEmpSearchResults([item])[0] ?? item;
    const tempId = this.getTempEmpLookupId(normalized);
    const name = this.pickLeftEmpText(
      normalized,
      'empName',
      'employeeName',
      'name',
      'userName',
      'employeeTempName',
      'fullName'
    );
    const mobile = this.pickLeftEmpText(
      normalized,
      'mobileNo',
      'mobileNumber',
      'mobile',
      'phoneNo',
      'contactNo',
      'contactNumber'
    );
    const aadhar = this.pickLeftEmpText(
      normalized,
      'aadharNo',
      'aadhaarNo',
      'aadharCardNo',
      'aadharNumber',
      'aadhaarNumber'
    );
    const pan = this.pickLeftEmpText(
      normalized,
      'panNo',
      'panCardNo',
      'panNumber',
      'pancardNo',
      'candidatePan'
    );
    const dob = this.pickLeftEmpText(
      normalized,
      'dob',
      'dateOfBirth',
      'employeeDob',
      'birthDate',
      'newEmpDob'
    );
    const expectedSalary = this.pickLeftEmpText(
      normalized,
      'expectedSalary',
      'salary',
      'expectedCTC',
      'salaryBudget',
      'currentSalary',
      'ctc'
    );
    const gender = normalizeMrfGenderValue(
      this.pickLeftEmpText(normalized, 'gender', 'employeeGender', 'sex')
    );
    const surname = this.pickLeftEmpText(normalized, 'surname', 'employeeSurname', 'lastName');
    const tempPayrollId = this.pickLeftEmpText(
      normalized,
      'tempPayrollId',
      'employeeTempId',
      'tempEmpId',
      'empTempId'
    );
    const prevChaitanyaId = this.pickLeftEmpText(
      normalized,
      'prevChaitanyaId',
      'previousChaitanyaId',
      'previousEmployeeId',
      'prevEmpId'
    );

    return {
      ...normalized,
      employeeTempId: tempId || tempPayrollId || normalized['employeeTempId'],
      tempEmpId: tempId || tempPayrollId || normalized['tempEmpId'],
      empTempId: tempId || tempPayrollId || normalized['empTempId'],
      ...(tempPayrollId ? { tempPayrollId } : {}),
      ...(name
        ? {
            empName: name,
            employeeName: name,
            newEmpName: name,
            newEmployeeName: name,
            candidateName: name,
          }
        : {}),
      ...(surname ? { surname, employeeSurname: surname, lastName: surname } : {}),
      ...(mobile ? { mobileNo: mobile, mobileNumber: mobile, candidateMobile: mobile } : {}),
      ...(aadhar
        ? {
            aadharNo: aadhar,
            newEmpAadhar: aadhar,
            aadharCardNo: aadhar,
            candidateAadhar: aadhar,
          }
        : {}),
      ...(pan ? { panNo: pan, panCardNo: pan, candidatePan: pan } : {}),
      ...(dob ? { dob, dateOfBirth: dob, employeeDob: dob, newEmpDob: dob } : {}),
      ...(expectedSalary ? { expectedSalary, expectedCTC: expectedSalary, salaryBudget: expectedSalary } : {}),
      ...(gender ? { gender, employeeGender: gender, sex: gender } : {}),
      ...(prevChaitanyaId ? { prevChaitanyaId } : {}),
    };
  }

  private normalizeTempEmpRecord(item: Record<string, unknown>): Record<string, unknown> {
    const record = this.normalizeLeftEmpRecord(item);
    const tempId = this.getTempEmpLookupId(record);
    return {
      ...record,
      employeeTempId: tempId || record['employeeTempId'],
      tempEmpId: tempId || record['tempEmpId'],
      empTempId: tempId || record['empTempId'],
    };
  }

  normalizeLeftEmpSearchResults(res: unknown): Record<string, unknown>[] {
    const rawList = this.extractLeftEmpArray(res);
    return rawList.map((item) => this.normalizeLeftEmpRecord(this.unwrapLeftEmpRecord(item)));
  }

  getLeftEmpLookupId(item: Record<string, unknown>): string {
    return this.pickLeftEmpText(
      item,
      'payrollId',
      'payRollId',
      'payroll',
      'employeeId',
      'empId',
      'uniqueId',
      'replacementEmpId',
      'replacementPayrollId',
      'id'
    );
  }

  findLeftEmpSearchMatch(
    list: Record<string, unknown>[],
    lookupId: string
  ): Record<string, unknown> | null {
    const id = String(lookupId ?? '').trim().toLowerCase();
    if (!id || !list.length) {
      return null;
    }

    const matchesId = (emp: Record<string, unknown>): boolean => {
      const candidates = [
        this.getLeftEmpLookupId(emp),
        emp['empId'],
        emp['employeeId'],
        emp['uniqueId'],
        emp['payrollId'],
        emp['payRollId'],
        emp['replacementEmpId'],
        emp['replacementPayrollId'],
      ]
        .map((val) => String(val ?? '').trim().toLowerCase())
        .filter(Boolean);
      return candidates.includes(id);
    };

    return list.find(matchesId) ?? list[0] ?? null;
  }

  findLeftEmpSearchMatchByAadhar(
    list: Record<string, unknown>[],
    aadharNo: string
  ): Record<string, unknown> | null {
    const digits = normalizeMrfAadhaarDigits(aadharNo);
    if (!digits || !list.length) {
      return null;
    }

    const matchesAadhar = (emp: Record<string, unknown>): boolean => {
      const candidates = [
        emp['aadharNo'],
        emp['aadhaarNo'],
        emp['aadharNumber'],
        emp['aadharCardNo'],
        emp['aadhaarNumber'],
      ]
        .map((val) => normalizeMrfAadhaarDigits(val))
        .filter(Boolean);
      return candidates.includes(digits);
    };

    return list.find(matchesAadhar) ?? list[0] ?? null;
  }

  private seedReplacementEmpId(
    data: Record<string, unknown>,
    lookupId: string
  ): Record<string, unknown> {
    const id = String(lookupId ?? '').trim();
    if (!id) {
      return data;
    }
    const numericId = this.toNumericReplacementEmpId(
      data['replacementEmpId'],
      data['uniqueId'],
      data['empId'],
      data['employeeId'],
      data['id']
    );
    const payrollId =
      this.pickLeftEmpText(data, 'payrollId', 'payRollId', 'replacementPayrollId') ||
      (/^\d+$/.test(id) ? '' : id);
    return {
      ...data,
      replacementEmpId: numericId ?? data['replacementEmpId'],
      replacementPayrollId: payrollId || String(data['replacementPayrollId'] ?? id),
    };
  }

  private toNumericReplacementEmpId(...values: unknown[]): number | null {
    for (const value of values) {
      if (value == null || value === '') {
        continue;
      }
      const text = String(value).trim();
      if (!text || text === '0' || !/^\d+$/.test(text)) {
        continue;
      }
      const num = Number(text);
      if (Number.isFinite(num) && num !== 0) {
        return num;
      }
    }
    return null;
  }

  /** Employee id used for replacement lookup (currentAndLeftEmpSearch). */
  resolveMrfReplacementEmpId(data: Record<string, unknown>): string {
    const value = data['replacementEmpId'];
    if (value != null && String(value).trim() !== '' && String(value) !== '0') {
      return String(value).trim();
    }
    return '';
  }

  /** Payroll id used for rehiring lookup (LeftEmpSearch). */
  resolveMrfRehiringPayrollId(data: Record<string, unknown>): string {
    for (const key of [
      'rehiringPayrollId',
      'prevChaitanyaId',
      'previousEmployeeId',
      'lastEmployeeId',
      'prevEmpId',
      'prevRehiringPayrollId',
    ]) {
      const value = data[key];
      if (value != null && String(value).trim() !== '' && String(value) !== '0') {
        return String(value).trim();
      }
    }
    return '';
  }

  /** Loads rehiring employee via LeftEmpSearch using rehiringPayrollId. */
  loadRehiringViaLeftEmpSearch(
    mrfData: Record<string, unknown>
  ): Observable<Record<string, unknown>> {
    const payrollId = this.resolveMrfRehiringPayrollId(mrfData);
    const aadharNo = resolveMrfRehiringAadharNo(mrfData);
    if (!payrollId && !aadharNo) {
      return of(mrfData);
    }

    return this.searchLeftEmployees(payrollId, { aadharNo: aadharNo || undefined }).pipe(
      map((res) => {
        const list = this.normalizeLeftEmpSearchResults(res);
        const match = payrollId
          ? this.findLeftEmpSearchMatch(list, payrollId)
          : this.findLeftEmpSearchMatchByAadhar(list, aadharNo);
        if (!match) {
          return {
            ...mrfData,
            ...(payrollId ? { rehiringPayrollId: payrollId } : {}),
          };
        }
        const resolvedId = this.getLeftEmpLookupId(match) || payrollId;
        return this.mergeMrfReplacementDetails(mrfData, match, resolvedId, {
          prevEmployeeId: payrollId || resolvedId,
          isRehiring: true,
          scope: 'previous',
        });
      }),
      catchError((err) => {
        console.error('LeftEmpSearch failed', err);
        return of({
          ...mrfData,
          ...(payrollId ? { rehiringPayrollId: payrollId } : {}),
        });
      })
    );
  }

  /** Loads replacement employee via currentAndLeftEmpSearch and merges into mrf view data. */
  loadReplacementViaCurrentAndLeftSearch(
    mrfData: Record<string, unknown>,
    lookupId: string,
    options?: {
      prevEmployeeId?: string;
      isRehiring?: boolean;
      scope?: 'previous' | 'replacement' | 'all';
    }
  ): Observable<Record<string, unknown>> {
    const id = String(lookupId ?? '').trim();
    if (!id) {
      return of(mrfData);
    }

    const campusId =
      mrfData['campusId'] != null ? String(mrfData['campusId']).trim() : '';

    return this.searchCurrentAndLeftEmployees(id, campusId || null).pipe(
      map((res) => {
        const list = this.normalizeLeftEmpSearchResults(res);
        const match = this.findLeftEmpSearchMatch(list, id);
        if (!match) {
          return this.seedReplacementEmpId(mrfData, id);
        }
        const resolvedId = this.getLeftEmpLookupId(match) || id;
        const merged = this.mergeMrfReplacementDetails(mrfData, match, resolvedId, {
          ...options,
          scope: options?.scope ?? 'replacement',
        });
        const numericId =
          this.toNumericReplacementEmpId(mrfData['replacementEmpId']) ??
          this.toNumericReplacementEmpId(
            match['uniqueId'],
            match['empId'],
            match['employeeId'],
            match['id']
          );
        if (numericId != null) {
          merged['replacementEmpId'] = numericId;
        }
        return this.seedReplacementEmpId(merged, resolvedId);
      }),
      catchError((err) => {
        console.error('currentAndLeftEmpSearch failed', err);
        return of(this.seedReplacementEmpId(mrfData, id));
      })
    );
  }

  /** Maps LeftEmpSearch row to replacement form fields (name, designation, LWD, etc.). */
  mapLeftEmpSearchToViewData(
    item: Record<string, unknown>,
    lookupId?: string
  ): Record<string, unknown> {
    const record = this.normalizeLeftEmpRecord(this.unwrapLeftEmpRecord(item));
    const id =
      String(lookupId ?? '').trim() ||
      this.pickLeftEmpText(record, 'payrollId', 'payRollId') ||
      this.pickLeftEmpText(record, 'employeeId', 'empId', 'uniqueId');
    const mapped = this.mapMrfReplacementToViewData(record, id);
    return { ...record, ...mapped };
  }

  private unwrapLeftEmpRecord(item: Record<string, unknown>): Record<string, unknown> {
    const nested =
      item['data'] ??
      item['employee'] ??
      item['employeeDetails'] ??
      item['empDetails'] ??
      item['leftEmployee'] ??
      item['tempEmployee'] ??
      item['tempEmp'] ??
      item['employeeTemp'] ??
      item['employeeTempDetails'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...item, ...(nested as Record<string, unknown>) };
    }
    return item;
  }

  private extractLeftEmpArray(res: unknown): Record<string, unknown>[] {
    if (Array.isArray(res)) {
      return res.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
    }
    if (!res || typeof res !== 'object') return [];
    const root = res as Record<string, unknown>;
    const data = (root['data'] ??
      root['result'] ??
      root['response'] ??
      root['employees'] ??
      root['employeeList'] ??
      root['leftEmployees'] ??
      root['tempEmployees'] ??
      root['tempEmpList'] ??
      root['tempEmployeeList'] ??
      root) as unknown;
    if (Array.isArray(data)) {
      return data.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
    }
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const nestedList =
        obj['employees'] ??
        obj['employeeList'] ??
        obj['leftEmployees'] ??
        obj['tempEmployees'] ??
        obj['tempEmpList'] ??
        obj['tempEmployeeList'] ??
        obj['list'] ??
        obj['results'] ??
        obj['content'];
      if (Array.isArray(nestedList)) {
        return nestedList.filter((item) => item && typeof item === 'object') as Record<
          string,
          unknown
        >[];
      }
      return [obj];
    }
    return [];
  }

  private pickLeftEmpText(item: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const val = item[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  private normalizeLeftEmpRecord(item: Record<string, unknown>): Record<string, unknown> {
    const payrollId = this.pickLeftEmpText(item, 'payrollId', 'payRollId', 'payroll');
    const empName = this.pickLeftEmpText(item, 'empName', 'userName', 'employeeName', 'name');
    const mobileNo = this.pickLeftEmpText(
      item,
      'mobileNo',
      'mobileNumber',
      'mobile',
      'phoneNo',
      'contactNo'
    );
    const designation = this.pickLeftEmpText(
      item,
      'designation',
      'desigName',
      'designationName',
      'empDesignation',
      'employeeDesignation',
      'desig'
    );
    const lastWorkingDate = this.pickLeftEmpText(
      item,
      'lastWorkingDate',
      'lastWorkingDay',
      'lastWorkDate',
      'lwd',
      'dateOfLeaving',
      'dol',
      'relievingDate'
    );

    return {
      ...item,
      payrollId: payrollId || item['payrollId'],
      payRollId: payrollId || item['payRollId'],
      empId: item['empId'] ?? item['employeeId'],
      employeeId: item['employeeId'] ?? payrollId,
      empName,
      userName: empName,
      employeeName: empName,
      mobileNo: mobileNo || item['mobileNo'],
      desigName: designation,
      designation,
      lastWorkingDate: lastWorkingDate || item['lastWorkingDate'],
      aadharNo: normalizeMrfAadhaarDigits(
        this.pickLeftEmpText(item, 'aadharNo', 'aadhaarNo', 'aadharNumber', 'aadharCardNo')
      ),
      dob: this.pickLeftEmpText(
        item,
        'dob',
        'dateOfBirth',
        'employeeDob',
        'birthDate'
      ),
    };
  }

  mapMrfReplacementToViewData(res: unknown, lookupId: string): Record<string, unknown> {
    const raw =
      (res as { data?: Record<string, unknown> })?.data ??
      (res as Record<string, unknown>);
    const data = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};

    const str = (...vals: unknown[]): string => {
      for (const v of vals) {
        if (v != null && String(v).trim() !== '' && String(v) !== '0') {
          return String(v).trim();
        }
      }
      return '';
    };

    const name = str(
      data['empName'],
      data['userName'],
      data['employeeName'],
      data['name'],
      data['fullName'],
      data['employeeFullName']
    );
    const desigName = str(
      data['designation'],
      data['desigName'],
      data['designationName'],
      data['empDesignation'],
      data['employeeDesignation'],
      data['desig'],
      data['replacementDesigName'],
      data['replacementDesignation']
    );
    const aadhar = str(data['aadharNo'], data['aadhaarNo'], data['aadharNumber'], data['aadharCardNo']);
    const mobile = str(data['mobileNo'], data['mobileNumber'], data['mobile'], data['phoneNo'], data['contactNo']);
    const pan = str(data['panNo'], data['panCardNo'], data['panNumber'], data['pancardNo']);

    return {
      replacementPayrollId: lookupId,
      replacementEmpId: str(
        data['payrollId'],
        data['payRollId'],
        data['employeeId'],
        data['empId'],
        data['uniqueId'],
        data['id'],
        lookupId
      ),
      replacementEmpName: name,
      replacementDesigName: desigName,
      replacementDesignation: desigName,
      aadharNo: aadhar,
      mobileNo: mobile,
      panNo: pan,
      lastWorkingDate: str(
        data['lastWorkingDate'],
        data['lastWorkingDay'],
        data['lastWorkDate'],
        data['lwd'],
        data['dateOfLeaving'],
        data['dol'],
        data['relievingDate']
      ),
      currentCTC: str(data['currentCTC'], data['ctc'], data['currentSalary'], data['prevRepEmpCtc'], data['prevRepEmpCTC']),
      prevRepEmpCtc: str(
        data['prevRepEmpCtc'],
        data['prevRepEmpCTC'],
        data['currentCTC'],
        data['ctc'],
        data['currentSalary']
      ),
      departmentName: str(data['deptName'], data['departmentName'], data['department']),
      desigName: str(data['designation'], data['desigName'], data['designationName']),
      dob: str(data['dob'], data['dateOfBirth'], data['employeeDob'], data['birthDate']),
    };
  }

  mergeMrfReplacementDetails(
    mrfData: Record<string, unknown>,
    res: unknown,
    lookupId: string,
    options?: {
      prevEmployeeId?: string;
      isRehiring?: boolean;
      scope?: 'previous' | 'replacement' | 'all';
    }
  ): Record<string, unknown> {
    const mapped = this.mapMrfReplacementToViewData(res, lookupId);
    const merged: Record<string, unknown> = { ...mrfData };
    const prevEmployeeId = String(options?.prevEmployeeId ?? '').trim();
    const isRehiring = !!options?.isRehiring;
    const scope = options?.scope ?? 'all';
    const replacementOnlyKeys = new Set([
      'replacementPayrollId',
      'replacementEmpId',
      'replacementEmpName',
      'replacementDesigName',
      'replacementDesignation',
      'lastWorkingDate',
      'currentCTC',
      'prevRepEmpCtc',
    ]);

    const pick = (...keys: string[]): string => {
      for (const key of keys) {
        const val = merged[key];
        if (val != null && String(val).trim() !== '' && String(val) !== '0') {
          return String(val).trim();
        }
      }
      return '';
    };

    if (scope === 'previous') {
      const prevId = prevEmployeeId || lookupId;
      if (prevId) {
        merged['prevChaitanyaId'] = String(merged['prevChaitanyaId'] ?? prevId);
        if (isRehiring) {
          merged['rehiringPayrollId'] = String(merged['rehiringPayrollId'] ?? prevId);
        }
      }
      const prevName = String(mapped['replacementEmpName'] ?? '').trim();
      const prevDesig = String(mapped['replacementDesigName'] ?? '').trim();
      if (prevName) merged['prevEmpName'] = prevName;
      if (prevDesig) merged['prevEmpDesignation'] = prevDesig;

      const prevAadhar = String(mapped['aadharNo'] ?? '').trim();
      if (isRehiring) {
        const exactAadhar = resolveMrfRehiringAadharPreferringSaved(merged, prevAadhar);
        if (exactAadhar) {
          merged['aadharNo'] = exactAadhar;
        }
      }
      const prevMobile = String(mapped['mobileNo'] ?? '').trim();
      if (prevMobile) {
        merged['mobileNo'] = prevMobile;
      }
      const prevDob = String(mapped['dob'] ?? '').trim();
      if (prevDob && isRehiring && !pick('dob', 'dateOfBirth', 'employeeDob', 'newEmpDob')) {
        merged['dob'] = prevDob;
      }

      return merged;
    }

    for (const [key, value] of Object.entries(mapped)) {
      if (scope === 'replacement' && !replacementOnlyKeys.has(key)) {
        continue;
      }
      if (key === 'replacementEmpId' && this.toNumericReplacementEmpId(merged['replacementEmpId'])) {
        continue;
      }
      if (value != null && String(value).trim() !== '' && String(value) !== '0') {
        merged[key] = value;
      }
    }

    if (scope === 'replacement') {
      return merged;
    }

    if (prevEmployeeId) {
      merged['prevChaitanyaId'] = String(merged['prevChaitanyaId'] ?? prevEmployeeId);
      const prevName = String(mapped['replacementEmpName'] ?? '').trim();
      const prevDesig = String(mapped['replacementDesigName'] ?? '').trim();
      if (prevName) merged['prevEmpName'] = prevName;
      if (prevDesig) merged['prevEmpDesignation'] = prevDesig;
    }

      return merged;
    }

  getMrfQualifications() {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/qualifications`, {
      headers: this.authHeaders(),
    }));
  }

  parseMrfQualificationsResponse(res: unknown): unknown[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;

    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
    if (Array.isArray(data)) return data;

    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      for (const key of [
        'qualifications',
        'qualificationList',
        'qualificationDetails',
        'educationQualifications',
        'list',
        'records',
        'items',
      ]) {
        if (Array.isArray(obj[key])) {
          return obj[key] as unknown[];
        }
      }
    }

    return [];
  }

  parseMrfReportingManagersResponse(res: unknown): unknown[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;

    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
    if (Array.isArray(data)) return data;

    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      for (const key of [
        'reportingManagers',
        'managers',
        'managerList',
        'reportingManagerDetails',
        'reportingManagerList',
        'list',
        'records',
        'items',
      ]) {
        if (Array.isArray(obj[key])) {
          return obj[key] as unknown[];
        }
      }
    }

    return [];
  }

  buildMrfReportingManagerOptionLabel(item: Record<string, unknown> | null | undefined): string {
    if (!item) return '';
    const name = String(
      item['reportingManagerName'] ??
        item['managerName'] ??
        item['empName'] ??
        item['employeeName'] ??
        item['userName'] ??
        item['name'] ??
        ''
    ).trim();
    const payrollId = String(
      item['payrollId'] ??
        item['payRollId'] ??
        item['payrollID'] ??
        item['managerPayrollId'] ??
        item['reportingManagerPayrollId'] ??
        ''
    ).trim();
    const desigName = String(
      item['desigName'] ??
        item['designation'] ??
        item['desig'] ??
        item['managerDesignation'] ??
        item['designationName'] ??
        ''
    ).trim();
    return [name, payrollId, desigName].filter(Boolean).join(' - ');
  }

  normalizeMrfReportingManagerList(res: unknown): Record<string, unknown>[] {
    return this.parseMrfReportingManagersResponse(res)
      .map((item) => this.normalizeMrfReportingManagerOption(item))
      .filter(
        (item): item is Record<string, unknown> =>
          item != null && !!String(item['reportingManagerName'] ?? '').trim()
      );
  }

  private normalizeMrfReportingManagerOption(item: unknown): Record<string, unknown> | null {
    if (typeof item === 'string') {
      const reportingManagerName = item.trim();
      return reportingManagerName
        ? { reportingManagerName, payrollId: '', desigName: '' }
        : null;
    }
    if (!item || typeof item !== 'object') {
      return null;
    }
    const record = item as Record<string, unknown>;
    const reportingManagerName = String(
      record['reportingManagerName'] ??
        record['managerName'] ??
        record['empName'] ??
        record['employeeName'] ??
        record['userName'] ??
        record['name'] ??
        ''
    ).trim();
    if (!reportingManagerName) {
      return null;
    }
    const payrollId = String(
      record['payrollId'] ??
        record['payRollId'] ??
        record['payrollID'] ??
        record['managerPayrollId'] ??
        record['reportingManagerPayrollId'] ??
        record['reportingManagerPayroll'] ??
        ''
    ).trim();
    const desigName = String(
      record['desigName'] ??
        record['designation'] ??
        record['desig'] ??
        record['managerDesignation'] ??
        record['designationName'] ??
        ''
    ).trim();
    const employeeIdRaw =
      record['reportingManagerId'] ??
      record['managerId'] ??
      record['employeeId'] ??
      record['empId'] ??
      record['id'];
    const managerId =
      employeeIdRaw != null && employeeIdRaw !== '' && String(employeeIdRaw) !== '0'
        ? Number(employeeIdRaw)
        : null;
    return {
      ...record,
      reportingManagerName,
      managerName: String(record['managerName'] ?? reportingManagerName).trim(),
      payrollId,
      desigName,
      managerId: managerId != null && !Number.isNaN(managerId) ? managerId : null,
      reportingManagerId:
        managerId != null && !Number.isNaN(managerId) ? managerId : null,
    };
  }

  getMrfReportingManagerDisplayFromData(
    data: Record<string, unknown>,
    managers: Record<string, unknown>[] = []
  ): string {
    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim();
        }
      }
      return '';
    };

    const name = pickString(
      data['reportingManagerName'],
      data['reportingManager'],
      data['managerName'],
      data['reportingManagerDesc']
    );
    let payrollId = pickString(
      data['reportingManagerPayrollId'],
      data['managerPayrollId'],
      data['reportingManagerPayroll'],
      data['reportingManagerPayRollId']
    );
    let desigName = pickString(
      data['reportingManagerDesigName'],
      data['managerDesigName'],
      data['managerDesignation'],
      data['reportingManagerDesignation']
    );

    const managerId = data['reportingManagerId'] ?? data['managerId'];
    let match: Record<string, unknown> | undefined;

    if (managerId != null && managerId !== '' && String(managerId) !== '0') {
      match = managers.find((manager) => {
        const id = Number(
          manager['reportingManagerId'] ??
            manager['managerId'] ??
            manager['employeeId'] ??
            manager['empId'] ??
            manager['id']
        );
        return !Number.isNaN(id) && id === Number(managerId);
      });
    }

    if (!match && name) {
      match = managers.find((manager) => {
        const managerName = String(
          manager['reportingManagerName'] ??
            manager['managerName'] ??
            manager['empName'] ??
            manager['employeeName'] ??
            manager['name'] ??
            ''
        )
          .trim()
          .toLowerCase();
        return managerName && managerName === name.toLowerCase();
      });
    }

    if (match) {
      payrollId =
        payrollId ||
        pickString(
          match['payrollId'],
          match['payRollId'],
          match['managerPayrollId'],
          match['reportingManagerPayrollId']
        );
      desigName =
        desigName ||
        pickString(
          match['desigName'],
          match['designation'],
          match['desig'],
          match['managerDesignation'],
          match['designationName']
        );
    }

    return this.buildMrfReportingManagerOptionLabel({
      reportingManagerName: name || match?.['reportingManagerName'] || match?.['managerName'],
      managerName: match?.['managerName'] ?? name,
      payrollId,
      payRollId: payrollId,
      managerPayrollId: payrollId,
      desigName,
      designation: match?.['designation'],
      desig: match?.['desig'],
      managerDesignation: match?.['managerDesignation'],
    });
  }

  /** GET .../mrf/reportingManagers?campusId= — reporting managers for selected campus. */
  getMrfReportingManagers(campusId?: string | number | null | unknown) {
    let url = `${this.baseUrl}${MRF_API}/reportingManagers`;
    const campus =
      campusId != null && (typeof campusId === 'string' || typeof campusId === 'number')
        ? String(campusId).trim()
        : '';
    if (campus) {
      url += `?campusId=${encodeURIComponent(campus)}`;
    }
    return this.mrfHttp(this.http.get(url, {
      headers: this.authHeaders(),
    }));
  }

  /** GET /employee/api/jwtV2/mrf/approvedByDetails */
  getMrfApprovedByDetails() {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/approvedByDetails`, {
      headers: this.authHeaders().set('Accept', 'application/json'),
    }));
  }

  parseMrfApprovedByDetailsResponse(res: unknown): MrfApprovedByOption[] {
    const data = (res as { data?: unknown })?.data ?? res;
    const rawList = Array.isArray(data)
      ? data
      : ((data as { approvedByDetails?: unknown[] })?.approvedByDetails ??
        (data as { reportingManagers?: unknown[] })?.reportingManagers ??
        (data as { managers?: unknown[] })?.managers ??
        (data as { managerList?: unknown[] })?.managerList ??
        []);
    return rawList
      .map((item) => this.normalizeMrfApprovedByOption(item))
      .filter((option) => !!option.name);
  }

  normalizeMrfApprovedByOption(item: unknown): MrfApprovedByOption {
    if (typeof item === 'string') {
      const name = item.trim();
      return {
        employeeId: null,
        employeeName: name,
        payrollId: '',
        desigId: null,
        desigName: '',
        name,
        label: name,
      };
    }

    const record = item as Record<string, unknown>;
    const employeeName = String(
      record['employeeName'] ??
        record['approvedByName'] ??
        record['approverName'] ??
        record['reportingManagerName'] ??
        record['managerName'] ??
        record['empName'] ??
        record['name'] ??
        ''
    ).trim();
    const payrollId = String(record['payrollId'] ?? record['payrollID'] ?? '').trim();
    const desigName = String(
      record['desigName'] ?? record['approvedByDesignation'] ?? record['designation'] ?? ''
    ).trim();
    const employeeIdRaw =
      record['employeeId'] ??
      record['reportingManagerId'] ??
      record['managerId'] ??
      record['empId'] ??
      record['id'];

    return {
      employeeId:
        employeeIdRaw != null && employeeIdRaw !== '' ? Number(employeeIdRaw) : null,
      employeeName,
      payrollId,
      desigId: record['desigId'] != null ? Number(record['desigId']) : null,
      desigName,
      name: employeeName,
      label: this.buildMrfApprovedByOptionLabel(employeeName, payrollId, desigName),
    };
  }

  getMrfApprovedByOptionLabel(option: MrfApprovedByOption | Record<string, unknown>): string {
    const label = (option as MrfApprovedByOption).label;
    if (label) return label;
    return String((option as Record<string, unknown>)['name'] ?? '').trim();
  }

  filterMrfApprovedByOptions(
    options: MrfApprovedByOption[],
    search: string
  ): MrfApprovedByOption[] {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(normalizedSearch) ||
        option.payrollId.toLowerCase().includes(normalizedSearch) ||
        option.desigName.toLowerCase().includes(normalizedSearch) ||
        option.label.toLowerCase().includes(normalizedSearch)
    );
  }

  resolveMrfApprovedByEmployeeId(
    selectedName: string,
    options: MrfApprovedByOption[],
    data?: Record<string, unknown>
  ): number | null {
    const normalizedName = selectedName.trim().toLowerCase();
    if (!normalizedName) return null;

    const match = options.find((option) => option.name.toLowerCase() === normalizedName);
    if (match?.employeeId != null) return match.employeeId;

    if (data) {
      const campus = this.extractMrfCampusApprovedByFields(data);
      if (
        campus.name &&
        campus.name.toLowerCase() === normalizedName &&
        campus.employeeId != null
      ) {
        return campus.employeeId;
      }
    }

    return null;
  }

  buildMrfApprovedByOptionFromFormData(data: Record<string, unknown>): MrfApprovedByOption | null {
    const campus = this.extractMrfCampusApprovedByFields(data);
    if (!campus.name) return null;

    return {
      employeeId: campus.employeeId,
      employeeName: campus.name,
      payrollId: '',
      desigId: null,
      desigName: campus.designation,
      name: campus.name,
      label: this.buildMrfApprovedByOptionLabel(campus.name, '', campus.designation),
    };
  }

  extractMrfManagementApprovedByFields(data: Record<string, unknown>): {
    employeeId: number | null;
    name: string;
    designation: string;
  } {
    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim();
        }
      }
      return '';
    };

    const name = pickString(
      data['approveByName'],
      data['managementApprovedByName'],
      data['approvedByManagementName'],
      data['misApprovedByName'],
      data['hrApprovedByName'],
      data['misApprovedByEmpName'],
      data['hrApprovedByEmpName']
    );

    const employeeIdRaw =
      data['approveBy'] ??
      data['managementApprovedById'] ??
      data['approvedByManagementId'] ??
      data['misApprovedById'] ??
      data['hrApprovedById'];

    let employeeId: number | null = null;
    if (employeeIdRaw != null && employeeIdRaw !== '') {
      const parsed = Number(employeeIdRaw);
      if (!Number.isNaN(parsed) && parsed > 0) {
        employeeId = parsed;
      }
    }

    const designation = pickString(
      data['approveByDesignation'],
      data['managementApprovedByDesignation'],
      data['approvedByManagementDesignation'],
      data['misApprovedByDesignation'],
      data['hrApprovedByDesignation']
    );

    return { employeeId, name, designation };
  }

  buildMrfApprovedByOptionsForManagementSelection(
    baseList: MrfApprovedByOption[],
    data: Record<string, unknown>
  ): MrfApprovedByOption[] {
    if (baseList.length) {
      return this.mergeMrfApprovedByManagementSelection(baseList, data);
    }

    const saved = this.buildMrfApprovedByManagementOptionFromFormData(data);
    return saved ? [saved] : [];
  }

  resolveMrfManagementApprovedBySelectValue(
    data: Record<string, unknown>,
    options: MrfApprovedByOption[]
  ): string {
    const management = this.extractMrfManagementApprovedByFields(data);

    if (management.employeeId != null) {
      const byId = options.find((option) => option.employeeId === management.employeeId);
      if (byId?.name) return byId.name;
    }

    if (management.name) {
      const byName = options.find(
        (option) => option.name.toLowerCase() === management.name.toLowerCase()
      );
      return byName?.name ?? management.name;
    }

    return '';
  }

  buildMrfApprovedByManagementOptionFromFormData(
    data: Record<string, unknown>
  ): MrfApprovedByOption | null {
    const management = this.extractMrfManagementApprovedByFields(data);
    if (!management.name && management.employeeId == null) {
      return null;
    }

    const employeeName =
      management.name ||
      (management.employeeId != null ? `Employee #${management.employeeId}` : '');

    return {
      employeeId: management.employeeId,
      employeeName,
      payrollId: '',
      desigId: null,
      desigName: management.designation,
      name: employeeName,
      label: this.buildMrfApprovedByOptionLabel(
        employeeName,
        '',
        management.designation
      ),
    };
  }

  mergeMrfApprovedByManagementSelection(
    options: MrfApprovedByOption[],
    data: Record<string, unknown>
  ): MrfApprovedByOption[] {
    const saved = this.buildMrfApprovedByManagementOptionFromFormData(data);
    if (!saved) return options;

    const index = options.findIndex(
      (option) =>
        (saved.employeeId != null && option.employeeId === saved.employeeId) ||
        (saved.name &&
          option.name.toLowerCase() === saved.name.toLowerCase())
    );

    if (index >= 0) {
      const existing = options[index];
      const merged: MrfApprovedByOption = {
        ...existing,
        employeeId: existing.employeeId ?? saved.employeeId,
        desigName: saved.desigName || existing.desigName,
        label: this.buildMrfApprovedByOptionLabel(
          existing.name || saved.name,
          existing.payrollId,
          saved.desigName || existing.desigName
        ),
      };
      const next = [...options];
      next[index] = merged;
      return next;
    }

    return [saved, ...options];
  }

  mergeMrfApprovedByCampusSelection(
    options: MrfApprovedByOption[],
    data: Record<string, unknown>
  ): MrfApprovedByOption[] {
    const saved = this.buildMrfApprovedByOptionFromFormData(data);
    if (!saved) return options;

    const index = options.findIndex(
      (option) =>
        (saved.employeeId != null && option.employeeId === saved.employeeId) ||
        option.name.toLowerCase() === saved.name.toLowerCase()
    );

    if (index >= 0) {
      const existing = options[index];
      const merged: MrfApprovedByOption = {
        ...existing,
        employeeId: existing.employeeId ?? saved.employeeId,
        desigName: saved.desigName || existing.desigName,
        label: this.buildMrfApprovedByOptionLabel(
          existing.employeeName,
          existing.payrollId,
          saved.desigName || existing.desigName
        ),
      };
      const next = [...options];
      next[index] = merged;
      return next;
    }

    return [saved, ...options];
  }

  resolveMrfManagementApprovedByNameFromData(
    data: Record<string, unknown>,
    options: MrfApprovedByOption[]
  ): string {
    const management = this.extractMrfManagementApprovedByFields(data);

    if (management.name) {
      return management.name;
    }

    if (management.employeeId != null) {
      const byId = options.find((option) => option.employeeId === management.employeeId);
      if (byId) return byId.name;
    }

    return '';
  }

  getMrfManagementApprovedByDisplayFromData(
    data: Record<string, unknown>,
    options: MrfApprovedByOption[]
  ): string {
    const management = this.extractMrfManagementApprovedByFields(data);

    if (management.name) {
      return management.designation
        ? `${management.name} - ${management.designation}`
        : management.name;
    }

    if (management.employeeId != null) {
      const byId = options.find((option) => option.employeeId === management.employeeId);
      if (byId) return this.getMrfApprovedByOptionLabel(byId);
    }

    return '';
  }

  resolveMrfManagementApprovedByIdFromData(data: Record<string, unknown>): number | null {
    return this.extractMrfManagementApprovedByFields(data).employeeId;
  }

  resolveMrfManagementApprovedByIdForSubmit(
    data: Record<string, unknown>,
    selectedName: string,
    options: MrfApprovedByOption[]
  ): number | null {
    const fromSelection = this.resolveMrfManagementApprovedByEmployeeId(selectedName, options);
    if (fromSelection != null) {
      return fromSelection;
    }
    return this.resolveMrfManagementApprovedByIdFromData(data);
  }

  resolveMrfManagementApprovedByEmployeeId(
    selectedName: string,
    options: MrfApprovedByOption[]
  ): number | null {
    const normalizedName = selectedName.trim().toLowerCase();
    if (!normalizedName) return null;

    const match = options.find((option) => option.name.toLowerCase() === normalizedName);
    return match?.employeeId ?? null;
  }

  getMrfApprovedByDisplayFromData(data: Record<string, unknown>): string {
    const campus = this.extractMrfCampusApprovedByFields(data);
    if (!campus.name) return '';
    return campus.designation ? `${campus.name} - ${campus.designation}` : campus.name;
  }

  extractMrfCampusApprovedByFields(data: Record<string, unknown>): {
    employeeId: number | null;
    name: string;
    designation: string;
  } {
    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim();
        }
      }
      return '';
    };

    const pickId = (...values: unknown[]): unknown => {
      for (const value of values) {
        if (value != null && value !== '' && String(value) !== '0') {
          return value;
        }
      }
      return null;
    };

    // Send management approver fields (approveBy*) are separate from campus (approvedBy*).
    // Prefer explicit campus/principal keys so MIS management approval does not overwrite display.
    const name =
      pickString(
        data['campusLevelApprovedByName'],
        data['approvedByCampusName'],
        data['campusApprovedByName'],
        data['campusLevelApproverName'],
        data['principalDeanName'],
        data['approvedByPrincipalName'],
        data['principalApproverName'],
        data['campusApproverName']
      ) ||
      pickString(data['approvedByName'], data['approvedByEmpName']);

    const designation =
      pickString(
        data['campusLevelApproverDesig'],
        data['approvedByCampusDesig'],
        data['campusApprovedByDesig'],
        data['campusLevelApproverDesignation'],
        data['principalDesigName'],
        data['principalDesignation'],
        data['approverDesignation'],
        data['campusApproverDesig']
      ) || pickString(data['approvedByDesignation']);

    const employeeIdRaw =
      pickId(
        data['campusLevelApproverId'],
        data['approvedByCampusEmpId'],
        data['campusApprovedByEmpId'],
        data['campusLevelApproverEmpId'],
        data['principalEmpId'],
        data['principalDeanEmpId'],
        data['approverEmpId'],
        data['campusApproverEmpId'],
        data['approvedBy'],
        data['approvedById'],
        data['approvedByEmpId']
      );

    let employeeId: number | null = null;
    if (employeeIdRaw != null && employeeIdRaw !== '') {
      const parsed = Number(employeeIdRaw);
      if (!Number.isNaN(parsed) && parsed > 0) {
        employeeId = parsed;
      }
    }

    return { employeeId, name, designation };
  }

  applyMrfCampusApprovedByFieldsToViewData(
    data: Record<string, unknown>,
    ...sources: Record<string, unknown>[]
  ): Record<string, unknown> {
    const fromData = this.extractMrfCampusApprovedByFields(data);
    const fromSources =
      sources.length > 0
        ? this.extractMrfCampusApprovedByFields(Object.assign({}, ...sources))
        : { employeeId: null, name: '', designation: '' };

    const campus = {
      name: fromData.name || fromSources.name,
      designation: fromData.designation || fromSources.designation,
      employeeId: fromData.employeeId ?? fromSources.employeeId,
    };
    const next: Record<string, unknown> = { ...data };

    if (campus.name) {
      next['campusLevelApprovedByName'] = campus.name;
    }
    if (campus.designation) {
      next['campusLevelApproverDesig'] = campus.designation;
    }
    if (campus.employeeId != null) {
      next['campusLevelApproverId'] = campus.employeeId;
    }

    return next;
  }

  applyMrfManagementApprovedByFieldsToViewData(
    data: Record<string, unknown>,
    ...sources: Record<string, unknown>[]
  ): Record<string, unknown> {
    const fromData = this.extractMrfManagementApprovedByFields(data);
    const fromSources =
      sources.length > 0
        ? this.extractMrfManagementApprovedByFields(Object.assign({}, ...sources))
        : { employeeId: null, name: '', designation: '' };

    const management = {
      employeeId: fromData.employeeId ?? fromSources.employeeId,
      name: fromData.name || fromSources.name,
      designation: fromData.designation || fromSources.designation,
    };
    const next: Record<string, unknown> = { ...data };

    if (management.employeeId != null) {
      next['approveBy'] = management.employeeId;
      next['managementApprovedById'] = management.employeeId;
    }
    if (management.name) {
      next['approveByName'] = management.name;
      next['managementApprovedByName'] = management.name;
    }
    if (management.designation) {
      next['approveByDesignation'] = management.designation;
      next['managementApprovedByDesignation'] = management.designation;
    }

    return next;
  }

  resolveMrfApprovedByNameFromData(
    data: Record<string, unknown>,
    options: MrfApprovedByOption[]
  ): string {
    const campus = this.extractMrfCampusApprovedByFields(data);
    if (campus.name) return campus.name;

    if (campus.employeeId != null) {
      const byId = options.find((option) => option.employeeId === campus.employeeId);
      if (byId) return byId.name;
    }

    return '';
  }

  private buildMrfApprovedByOptionLabel(
    employeeName: string,
    payrollId: string,
    desigName: string
  ): string {
    const labelParts = [employeeName, payrollId, desigName].filter(Boolean);
    return labelParts.join(' - ') || employeeName;
  }

  getMrfFormById(mrfId: string | number) {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/getMrfForm?mrfId=${encodeURIComponent(String(mrfId))}`, {
      headers: this.authHeaders(),
    }));
  }

  /** GET /employee/api/jwtV2/mrf/getRequests?mrfFormId={id} */
  getMrfRequests(mrfFormId: string | number) {
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/getRequests?mrfFormId=${encodeURIComponent(String(mrfFormId))}`,
      {
        headers: this.authHeaders().set('Accept', '*/*'),
      }
    ));
  }

  getMrfRequestsByHr(mrfFormId: string | number) {
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/getRequestsByHr?mrfFormId=${encodeURIComponent(String(mrfFormId))}`,
      { headers: this.authHeaders().set('Accept', '*/*') }
    ));
  }

  /** Returns getRequests API payload without field remapping. */
  parseMrfRequestsResponse(res: unknown): Record<string, unknown> {
    return this.extractMrfRequestsRaw(res);
  }

  /** Parses GET .../getRequests?mrfFormId= response for read-only review screens. */
  normalizeMrfRequestsResponse(res: unknown): Record<string, unknown> {
    const raw = this.extractMrfRequestsRaw(res);
    const mapped = this.mapMrfRequestsToViewData(raw);
    return this.applyMrfManagementApprovedByFieldsToViewData(
      this.applyMrfCampusApprovedByFieldsToViewData(mapped, raw),
      raw
    );
  }

  private extractMrfRequestsRaw(res: unknown): Record<string, unknown> {
    if (!res || typeof res !== 'object') return {};
    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as
      | Record<string, unknown>
      | unknown[];

    if (Array.isArray(data)) {
      return (data[0] as Record<string, unknown>) ?? {};
    }
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const nested =
        obj['mrfForm'] ??
        obj['mrfRequest'] ??
        obj['request'] ??
        obj['mrfFormDto'] ??
        obj['mrfFormData'] ??
        obj['formData'] ??
        obj['details'];
      const assessmentRaw =
        obj['employeeAssessment'] ??
        obj['employeeAssessmentDto'] ??
        obj['empAssessment'] ??
        obj['assessment'];
      const assessment =
        assessmentRaw && typeof assessmentRaw === 'object' && !Array.isArray(assessmentRaw)
          ? (assessmentRaw as Record<string, unknown>)
          : {};
      const misReview = this.extractMrfReviewObject(
        obj['misReview'] ?? obj['misReviewDto'] ?? obj['mrfMisReview'] ?? obj['misReviewDetails']
      );
      const hrReview = this.extractMrfReviewObject(obj['hrReview'] ?? obj['hrReviewDto']);
      const review = { ...misReview, ...hrReview };
      const mergedNested =
        nested && typeof nested === 'object' && !Array.isArray(nested)
          ? { ...obj, ...(nested as Record<string, unknown>), ...review }
          : { ...obj, ...review };
      return {
        ...mergedNested,
        ...assessment,
        ...this.buildMrfManagementReviewViewFields(review),
      };
    }
    return {};
  }

  private extractMrfReviewObject(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  private buildMrfManagementReviewViewFields(
    review: Record<string, unknown>
  ): Record<string, unknown> {
    if (!Object.keys(review).length) {
    return {};
    }

    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim();
        }
      }
      return '';
    };

    const next: Record<string, unknown> = {};

    const managementId =
      review['approveBy'] ?? review['approveById'] ?? review['approveByEmpId'];
    if (managementId != null && managementId !== '') {
      next['approveBy'] = managementId;
      next['managementApprovedById'] = managementId;
    }

    const managementName = pickString(
      review['approveByName'],
      review['approveByEmpName'],
      review['managementApprovedByName'],
      review['approvedByManagementName']
    );
    if (managementName) {
      next['approveByName'] = managementName;
      next['managementApprovedByName'] = managementName;
    }

    const managementDesig = pickString(
      review['approveByDesignation'],
      review['approvedByManagementDesignation'],
      review['managementApprovedByDesignation']
    );
    if (managementDesig) {
      next['approveByDesignation'] = managementDesig;
      next['managementApprovedByDesignation'] = managementDesig;
    }

    const workDescription = pickString(review['workDescription'], review['workDesc']);
    if (workDescription) {
      next['workDescription'] = workDescription;
    }

    const justification = pickString(review['justification']);
    if (justification) {
      next['justification'] = justification;
    }

    if (review['isRehirePolicy'] != null) {
      next['isRehirePolicy'] = review['isRehirePolicy'];
    }
    if (review['isRehireCost'] != null) {
      next['isRehireCost'] = review['isRehireCost'];
    }
    if (review['isBudgetPosition'] != null) {
      next['isBudgetPosition'] = review['isBudgetPosition'];
    }
    if (review['isJobDescription'] != null) {
      next['isJobDescription'] = review['isJobDescription'];
    }

    return next;
  }

  /** Maps API field names to template keys used in mrf-user2 / principal-dean. */
  private mapMrfRequestsToViewData(raw: Record<string, unknown>): Record<string, unknown> {
    const str = (...vals: unknown[]): string => {
      for (const v of vals) {
        if (v != null && String(v).trim() !== '' && String(v) !== '0') {
          return String(v).trim();
        }
      }
      return '';
    };

    const nestedRaw =
      raw['newEmployee'] ??
      raw['newEmployeeDto'] ??
      raw['candidate'] ??
      raw['candidateDetails'] ??
      raw['newEmpDetails'];
    const nested =
      nestedRaw && typeof nestedRaw === 'object' && !Array.isArray(nestedRaw)
        ? (nestedRaw as Record<string, unknown>)
        : {};
    const assessmentRaw =
      raw['employeeAssessment'] ??
      raw['employeeAssessmentDto'] ??
      raw['empAssessment'] ??
      raw['assessment'];
    const assessment =
      assessmentRaw && typeof assessmentRaw === 'object' && !Array.isArray(assessmentRaw)
        ? (assessmentRaw as Record<string, unknown>)
        : {};

    const positionRaw = str(raw['positionType'], raw['posType'], raw['position']);
    const positionType = positionRaw.includes('REPLACEMENT')
      ? 'Replacement'
      : positionRaw.includes('NEW')
        ? 'New Position'
        : positionRaw;
    const yearsOfExperience = resolveMrfYearsOfExperience(raw);
    const employeeTempIdValue = str(
      raw['employeeTempId'],
      raw['empTempId'],
      raw['tempEmpId'],
      raw['tempPayrollId'],
      assessment['employeeTempId'],
      assessment['empTempId'],
      assessment['tempEmpId'],
      assessment['tempPayrollId']
    );
    const tempPayrollIdValue = resolveMrfTempPayrollId({
      ...raw,
      employeeTempId: employeeTempIdValue,
      ...(assessment as Record<string, unknown>),
    });

    return {
      ...raw,
      mrfFormId: raw['mrfFormId'] ?? raw['requestId'] ?? raw['id'],
      campusId: raw['campusId'] ?? raw['CampusId'],
      campusName: str(raw['campusName'], raw['campus'], raw['campusTitle']),
      empType: str(raw['empType'], raw['employeeType'], raw['empTypeName']),
      empTypeId: raw['empTypeId'] ?? raw['employeeTypeId'],
      roleType: str(raw['roleType'], raw['teachingType'], raw['TeachType'], raw['teachType']),
      typeId: raw['typeId'] ?? raw['teachTypeId'] ?? raw['teachingTypeId'],
      teachTypeId: raw['teachTypeId'] ?? raw['typeId'] ?? raw['teachingTypeId'],
      departmentName: str(raw['departmentName'], raw['deptName'], raw['department']),
      deptId: raw['deptId'] ?? raw['departmentId'],
      desigName: str(raw['desigName'], raw['designation'], raw['designationName']),
      desigId: raw['desigId'] ?? raw['designationId'],
      subName: str(raw['subName'], raw['subjectName'], raw['subject']),
      subId: raw['subId'] ?? raw['subjectId'],
      workLoad: str(raw['workLoad'], raw['workload']),
      positionType,
      hiringReason: str(raw['hiringReason'], raw['reasonForHiring'], raw['hiringReasonText']),
      reasonDescription: str(
        raw['reasonDescription'],
        raw['hiringReasonDesc'],
        raw['reasonDesc'],
        raw['hiringReasonDescription']
      ),
      replacementEmpId: str(raw['replacementEmpId']),
      replacementPayrollId: str(
        raw['replacementPayrollId'],
        raw['replacementEmpPayrollId'],
        raw['replacementEmployeePayrollId']
      ),
      replacementEmpName: str(
        raw['replacementEmpName'],
        raw['replacementEmployeeName'],
        raw['replacementName'],
        raw['replacementEmp']
      ),
      replacementDesigName: str(
        raw['replacementDesigName'],
        raw['replacementDesignation'],
        raw['replacementDesig']
      ),
      newPositionDesigName: str(
        raw['newPositionDesigName'],
        raw['positionDesignation'],
        raw['posDesigName'],
        raw['newPosDesignation'],
        raw['newPositionDesignation']
      ),
      newPositionEmpId: str(raw['newPositionEmpId'], raw['newPosEmpId'], raw['employeeId']),
      newPositionEmpName: str(
        raw['newPositionEmpName'],
        raw['newPosEmpName'],
        raw['newEmployeeName'],
        raw['candidateName']
      ),
      lastWorkingDate: str(raw['lastWorkingDate'], raw['lastWorkingDay'], raw['lwd']),
      currentCTC: str(
        raw['currentCTC'],
        raw['ctc'],
        raw['currentSalary'],
        raw['prevRepEmpCtc'],
        raw['prevRepEmpCTC']
      ),
      prevRepEmpCtc: str(
        raw['prevRepEmpCtc'],
        raw['prevRepEmpCTC'],
        raw['prevRepEmpCtcAmount'],
        raw['previousReplacementCtc'],
        raw['currentCTC'],
        raw['ctc'],
        raw['currentSalary']
      ),
      isReHiring: raw['isReHiring'] ?? raw['isRehiring'] ?? raw['rehiring'] ?? false,
      isRehiring: raw['isRehiring'] ?? raw['isReHiring'] ?? raw['rehiring'] ?? false,
      rehiringPayrollId: str(
        raw['rehiringPayrollId'],
        raw['prevChaitanyaId'],
        raw['previousEmployeeId'],
        raw['lastEmployeeId'],
        raw['prevEmpId']
      ),
      prevChaitanyaId: str(
        raw['prevChaitanyaId'],
        raw['previousEmployeeId'],
        raw['lastEmployeeId'],
        raw['prevEmpId']
      ),
      aadharNo: resolveMrfRehiringAadharPreferringSaved(
        raw as Record<string, unknown>,
        str(raw['aadharNo'], raw['aadhaarNo'], raw['aadharNumber'])
      ),
      qualificationName: str(
        raw['qualificationName'],
        raw['qualification'],
        raw['qualificationDesc']
      ),
      qualificationId: raw['qualificationId'],
      additionalQualification: str(
        raw['additionalQualification'],
        raw['additionalQual'],
        raw['additionalQualificationName']
      ),
      yearsOfExp: yearsOfExperience,
      desiredExperience: yearsOfExperience,
      skills: str(raw['skills'], raw['skill'], raw['skillsName']),
      salaryBudget: str(raw['salaryBudget'], raw['salary'], raw['salaryBudgetPosition']),
      costCenter: str(raw['costCenter'], raw['costCenterName'], raw['costCenterDesc']),
      reportingManagerName: str(
        raw['reportingManagerName'],
        raw['reportingManager'],
        raw['managerName'],
        raw['reportingManagerDesc']
      ),
      reportingManagerId: raw['reportingManagerId'] ?? raw['managerId'],
      reportingManagerPayrollId: str(
        raw['reportingManagerPayrollId'],
        raw['managerPayrollId'],
        raw['reportingManagerPayroll'],
        raw['reportingManagerPayRollId'],
        raw['payrollId']
      ),
      reportingManagerDesigName: str(
        raw['reportingManagerDesigName'],
        raw['managerDesigName'],
        raw['managerDesignation'],
        raw['reportingManagerDesignation']
      ),
      approvedBy: raw['approvedBy'] ?? raw['approvedById'] ?? raw['campusLevelApproverId'],
      approvedByName: str(
        raw['campusLevelApprovedByName'],
        raw['approvedByCampusName'],
        raw['campusApprovedByName'],
        raw['campusLevelApproverName'],
        raw['principalDeanName'],
        raw['approvedByPrincipalName'],
        raw['approvedByName'],
        raw['approvedByEmpName']
      ),
      approvedByDesignation: str(
        raw['campusLevelApproverDesig'],
        raw['approvedByCampusDesig'],
        raw['campusApprovedByDesig'],
        raw['campusLevelApproverDesignation'],
        raw['principalDesigName'],
        raw['principalDesignation'],
        raw['approvedByDesignation']
      ),
      approvedById: raw['approvedById'] ?? raw['approvedBy'],
      campusLevelApprovedByName: str(
        raw['campusLevelApprovedByName'],
        raw['approvedByCampusName'],
        raw['campusApprovedByName'],
        raw['campusLevelApproverName'],
        raw['principalDeanName'],
        raw['approvedByPrincipalName'],
        raw['principalApproverName'],
        raw['campusApproverName'],
        raw['approvedByName'],
        raw['approvedByEmpName']
      ),
      campusLevelApproverId: str(
        raw['campusLevelApproverId'],
        raw['approvedByCampusEmpId'],
        raw['campusApprovedByEmpId'],
        raw['campusLevelApproverEmpId'],
        raw['principalEmpId'],
        raw['principalDeanEmpId'],
        raw['approverEmpId'],
        raw['campusApproverEmpId'],
        raw['approvedBy'],
        raw['approvedById'],
        raw['approvedByEmpId']
      ),
      campusLevelApproverDesig: str(
        raw['campusLevelApproverDesig'],
        raw['approvedByCampusDesig'],
        raw['campusApprovedByDesig'],
        raw['campusLevelApproverDesignation'],
        raw['principalDesigName'],
        raw['principalDesignation'],
        raw['approverDesignation'],
        raw['campusApproverDesig'],
        raw['approvedByDesignation']
      ),
      newEmpName: str(
        raw['newEmpName'],
        raw['newEmployeeName'],
        raw['candidateName'],
        raw['employeeName'],
        assessment['employeeName'],
        nested['newEmpName'],
        nested['name'],
        nested['candidateName']
      ),
      surname: str(
        raw['surname'],
        raw['employeeSurname'],
        raw['lastName'],
        assessment['surname'],
        assessment['employeeSurname'],
        assessment['lastName'],
        nested['surname'],
        nested['employeeSurname'],
        nested['lastName']
      ),
      employeeSurname: str(
        raw['surname'],
        raw['employeeSurname'],
        raw['lastName'],
        assessment['surname'],
        assessment['employeeSurname'],
        assessment['lastName'],
        nested['surname']
      ),
      mobileNo: str(
        raw['mobileNo'],
        raw['mobileNumber'],
        raw['candidateMobile'],
        raw['mobile'],
        raw['phoneNo'],
        raw['contactNo'],
        raw['contactNumber'],
        assessment['mobileNo'],
        assessment['mobileNumber'],
        nested['mobileNo'],
        nested['mobileNumber'],
        nested['mobile'],
        nested['phoneNo'],
        nested['contactNo']
      ),
      newEmpAadhar: str(
        raw['newEmpAadhar'],
        raw['aadharCardNo'],
        raw['candidateAadhar'],
        raw['newEmployeeAadhar'],
        assessment['aadhaarNo'],
        assessment['aadharNo'],
        nested['newEmpAadhar'],
        nested['aadharCardNo'],
        nested['aadhaarNo'],
        nested['aadharNo']
      ),
      panNo: str(
        raw['panNo'],
        raw['panCardNo'],
        raw['candidatePan'],
        raw['panNumber'],
        raw['panCardNumber'],
        raw['pancardNo'],
        assessment['panNo'],
        assessment['panCardNo'],
        nested['panNo'],
        nested['panCardNo'],
        nested['panNumber'],
        nested['candidatePan']
      ),
      examTaken:
        raw['examTaken'] ??
        raw['isExamTaken'] ??
        raw['examTakenFlag'] ??
        assessment['examTaken'] ??
        assessment['isExamTaken'],
      dob: str(
        raw['dob'],
        raw['dateOfBirth'],
        raw['employeeDob'],
        raw['newEmpDob'],
        assessment['dob'],
        assessment['dateOfBirth'],
        nested['dob']
      ),
      employeeTempId: employeeTempIdValue,
      empTempId: employeeTempIdValue || tempPayrollIdValue,
      tempEmpId: employeeTempIdValue || tempPayrollIdValue,
      tempPayrollId: tempPayrollIdValue || employeeTempIdValue,
      expectedSalary: str(
        raw['expectedSalary'],
        raw['salaryBudget'],
        raw['salary'],
        raw['expectedCTC'],
        raw['expectedSal'],
        assessment['expectedSalary'],
        assessment['expectedCTC'],
        assessment['expectedSal']
      ),
      gender: normalizeMrfGenderValue(
        str(
          raw['gender'],
          raw['employeeGender'],
          raw['sex'],
          assessment['gender'],
          assessment['employeeGender'],
          assessment['sex']
        )
      ),
      noOfHeadcount: raw['noOfHeadcount'] ?? raw['headcount'] ?? raw['noOfPositions'] ?? 1,
      entityName: str(raw['entityName'], raw['entity'], raw['campusEntity']),
      mrfStatus: str(raw['mrfStatus']),
      onboardingStatus: str(raw['onboardingStatus']),
      mrfFormStatus: str(
        raw['mrfFormStatus'],
        raw['mrfStatus'],
        raw['status'],
        raw['formStatus'],
        raw['statusName']
      ),
      workDescription: str(raw['workDescription'], raw['workDesc']),
      justification: str(raw['justification']),
      approveBy: raw['approveBy'],
      approveByName: str(
        raw['approveByName'],
        raw['managementApprovedByName'],
        raw['approvedByManagementName'],
        raw['misApprovedByName'],
        raw['hrApprovedByName']
      ),
      approveByDesignation: str(
        raw['approveByDesignation'],
        raw['managementApprovedByDesignation'],
        raw['approvedByManagementDesignation'],
        raw['misApprovedByDesignation'],
        raw['hrApprovedByDesignation']
      ),
      managementApprovedById:
        raw['approveBy'] ??
        raw['managementApprovedById'] ??
        raw['approvedByManagementId'] ??
        raw['misApprovedById'] ??
        raw['hrApprovedById'],
      managementApprovedByName: str(
        raw['approveByName'],
        raw['managementApprovedByName'],
        raw['approvedByManagementName'],
        raw['misApprovedByName'],
        raw['hrApprovedByName']
      ),
      managementApprovedByDesignation: str(
        raw['approveByDesignation'],
        raw['managementApprovedByDesignation'],
        raw['approvedByManagementDesignation'],
        raw['misApprovedByDesignation'],
        raw['hrApprovedByDesignation']
      ),
      isRehirePolicy: raw['isRehirePolicy'] ?? raw['rehirePolicyGuidelines'],
      isRehireCost: raw['isRehireCost'] ?? raw['withinRehireCost'],
      isBudgetPosition: raw['isBudgetPosition'] ?? raw['isBudgetedPosition'],
      isJobDescription: raw['isJobDescription'] ?? raw['jobDescriptionAttached'],
    };
  }

  parseEmployeeAssessmentResponse(res: unknown): Record<string, unknown> {
    if (res == null) return {};
    if (typeof res === 'string') {
      const trimmed = res.trim();
      if (!trimmed) return {};
      try {
        return this.parseEmployeeAssessmentResponse(JSON.parse(trimmed));
      } catch {
        return {};
      }
    }
    if (typeof res !== 'object') return {};

    const root = res as Record<string, unknown>;
    let data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as
      | Record<string, unknown>
      | unknown[];

    if (Array.isArray(data)) {
      data = (data[0] as Record<string, unknown>) ?? {};
    }
    if (!data || typeof data !== 'object') return {};

    const record = data as Record<string, unknown>;
    const nested =
      record['employeeAssessment'] ??
      record['employeeAssessmentDto'] ??
      record['empAssessment'] ??
      record['assessment'] ??
      record['employeeResponse'];

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...record, ...(nested as Record<string, unknown>) };
    }

    return record;
  }

  /** Builds dashboard success text after saveEmployeeAssessment (APPROVED). */
  buildSaveEmployeeAssessmentApprovalMessage(res: unknown): string {
    const base = 'MRF approved successfully.';
    const parsed = this.parseEmployeeAssessmentResponse(res);
    const tempPayrollId = this.resolveTempPayrollIdFromSaveEmployeeAssessmentResponse(res, parsed);

    if (tempPayrollId) {
      return `${base} Temp Payroll ID: ${tempPayrollId}`;
    }

    return base;
  }

  private resolveTempPayrollIdFromSaveEmployeeAssessmentResponse(
    res: unknown,
    parsed: Record<string, unknown>
  ): string {
    const fromFields = resolveMrfTempPayrollId(parsed);
    if (fromFields) {
      return fromFields;
    }

    const employeeResponse = parsed['employeeResponse'];
    if (employeeResponse && typeof employeeResponse === 'object' && !Array.isArray(employeeResponse)) {
      const fromEmployeeResponse = resolveMrfTempPayrollId(
        employeeResponse as Record<string, unknown>
      );
      if (fromEmployeeResponse) {
        return fromEmployeeResponse;
      }

      const employeeMessage = String(
        (employeeResponse as Record<string, unknown>)['message'] ?? ''
      ).trim();
      const fromEmployeeMessage = this.extractMrfTempPayrollIdFromText(employeeMessage);
      if (fromEmployeeMessage) {
        return fromEmployeeMessage;
      }
    }

    const message = String(parsed['message'] ?? '').trim();
    const fromMessage = this.extractMrfTempPayrollIdFromText(message);
    if (fromMessage) {
      return fromMessage;
    }

    if (typeof res === 'string') {
      return this.extractMrfTempPayrollIdFromText(res.trim());
    }

    if (res && typeof res === 'object') {
      const root = res as Record<string, unknown>;
      const rootEmployeeResponse = root['employeeResponse'];
      if (
        rootEmployeeResponse &&
        typeof rootEmployeeResponse === 'object' &&
        !Array.isArray(rootEmployeeResponse)
      ) {
        const fromRootEmployeeResponse = resolveMrfTempPayrollId(
          rootEmployeeResponse as Record<string, unknown>
        );
        if (fromRootEmployeeResponse) {
          return fromRootEmployeeResponse;
        }
      }

      const rootMessage = String(root['message'] ?? '').trim();
      return this.extractMrfTempPayrollIdFromText(rootMessage);
    }

    return '';
  }

  private extractMrfTempPayrollIdFromText(text: string): string {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) {
      return '';
    }

    const labeledMatch = trimmed.match(
      /temp\s*payroll\s*id\s*[:.]?\s*([A-Z0-9_-]+)/i
    );
    if (labeledMatch?.[1]) {
      return labeledMatch[1].trim();
    }

    const tempMatch = trimmed.match(/\bTEMP[\w\d]+\b/i);
    if (tempMatch?.[0]) {
      return tempMatch[0].trim();
    }

    if (/^[A-Z0-9_-]{4,}$/i.test(trimmed)) {
      return trimmed;
    }

    return '';
  }

  mergeEmployeeAssessmentIntoViewData(
    data: Record<string, unknown>,
    assessmentRes: unknown
  ): Record<string, unknown> {
    if (assessmentRes == null) return data;

    const mapped = this.mapEmployeeAssessmentToViewData(
      this.parseEmployeeAssessmentResponse(assessmentRes)
    );
    const merged: Record<string, unknown> = { ...data };

    for (const [key, value] of Object.entries(mapped)) {
      if (typeof value === 'boolean') {
        merged[key] = value;
        continue;
      }
      if (value == null || String(value).trim() === '' || String(value) === '0') {
        continue;
      }
      merged[key] = value;
    }

    return merged;
  }

  mapEmployeeAssessmentToViewData(raw: Record<string, unknown>): Record<string, unknown> {
    const str = (...vals: unknown[]): string => {
      for (const v of vals) {
        if (v != null && String(v).trim() !== '' && String(v) !== '0') {
          return String(v).trim();
        }
      }
      return '';
    };
    const employeeName = str(raw['employeeName'], raw['empName'], raw['name']);
    const surname = str(raw['surname'], raw['employeeSurname'], raw['lastName']);
    const mobileNo = str(raw['mobileNo'], raw['mobileNumber'], raw['mobile']);
    const aadhaarNo = str(raw['aadhaarNo'], raw['aadharNo'], raw['aadharCardNo']);
    const panNo = str(raw['panNo'], raw['panCardNo'], raw['panNumber']);
    const dob = str(raw['dob'], raw['dateOfBirth'], raw['employeeDob'], raw['newEmpDob']);
    const employeeTempId = str(
      raw['employeeTempId'],
      raw['empTempId'],
      raw['tempEmpId'],
      raw['tempPayrollId']
    );
    const tempPayrollId = resolveMrfTempPayrollId(raw) || employeeTempId;
    const expectedSalary = str(
      raw['expectedSalary'],
      raw['salaryBudget'],
      raw['salary'],
      raw['expectedCTC'],
      raw['expectedSal']
    );
    const gender = normalizeMrfGenderValue(str(raw['gender'], raw['employeeGender'], raw['sex']));
    const prevChaitanyaId = str(
      raw['prevChaitanyaId'],
      raw['previousChaitanyaId'],
      raw['previousEmployeeId'],
      raw['prevEmpId']
    );
    return {
      employeeName,
      newEmpName: employeeName,
      newEmployeeName: employeeName,
      candidateName: employeeName,
      surname,
      employeeSurname: surname,
      lastName: surname,
      mobileNo,
      mobileNumber: mobileNo,
      aadhaarNo,
      aadharNo: aadhaarNo,
      newEmpAadhar: aadhaarNo,
      aadharCardNo: aadhaarNo,
      candidateAadhar: aadhaarNo,
      panNo,
      panCardNo: panNo,
      dob,
      dateOfBirth: dob,
      employeeDob: dob,
      newEmpDob: dob,
      employeeTempId: tempPayrollId || employeeTempId,
      empTempId: tempPayrollId || employeeTempId,
      tempEmpId: tempPayrollId || employeeTempId,
      tempPayrollId,
      expectedSalary,
      expectedCTC: expectedSalary,
      salaryBudget: expectedSalary,
      expectedSal: expectedSalary,
      examTaken: raw['examTaken'] ?? raw['isExamTaken'] ?? raw['examTakenFlag'],
      gender,
      employeeGender: gender,
      sex: gender,
      ...(prevChaitanyaId ? { prevChaitanyaId } : {}),
    };
  }

  getEmployeeAssessment(mrfFormId: string | number) {
    return this.mrfHttp(this.http.get(
      `${this.baseUrl}${MRF_API}/getEmployeeAssessment?mrfFormId=${encodeURIComponent(String(mrfFormId))}`,
      { headers: this.authHeaders().set('Accept', 'application/json') }
    ));
  }

  getMrfDashboard(filters?: MrfLocationFilters) {
    return this.mrfHttp(this.http.get(`${this.masterDomainUrl}${MRF_API}/dashboard${this.buildFilterQuery(filters)}`, {
      headers: this.authHeaders(),
    }));
  }

  /** GET /mrf/dashboard/dateWise?month=7&year=2026 — summary counts for a calendar month (1–12). */
  getMrfDashboardDateWise(month: number, year: number, filters?: MrfLocationFilters) {
    const monthQuery = `month=${encodeURIComponent(String(month))}&year=${encodeURIComponent(String(year))}`;
    return this.mrfHttp(
      this.http.get(`${this.masterDomainUrl}${MRF_API}/dashboard/dateWise${this.buildFilterQuery(filters, monthQuery)}`, {
        headers: this.authHeaders().set('Accept', '*/*'),
      })
    );
  }

  getMrfTrackHistory(mrfFormStatus: string, filters?: MrfLocationFilters) {
    const statusQuery = `mrfFormStatus=${encodeURIComponent(mrfFormStatus)}`;
    const filterQuery = this.buildFilterQuery(filters, statusQuery);
    return this.mrfHttp(this.http.get(`${this.baseUrl}${MRF_API}/trackHistory${filterQuery}`, {
      headers: this.authHeaders(),
    }));
  }

  getMrfTrackHistoryWithFilters(payload: MrfTrackHistoryFilterPayload) {
    return this.mrfHttp(this.http.post(
      `${this.masterDomainUrl}/employee/api/jwtV2/mrf/v2/getMRFTrackHistoryWithFilters`,
      payload,
      {
        headers: this.authHeaders()
          .set('Content-Type', 'application/json')
          .set('Accept', 'application/json'),
      }
    ));
  }

  /** POST /employee/api/jwtV2/mrf/CoustomizeFilters — customize-filter dashboard search. */
  postMrfCustomizeFilters(payload: MrfCustomizeFilterPayload) {
    return this.mrfHttp(this.http.post(
      `${this.masterDomainUrl}${MRF_API}/CoustomizeFilters`,
      payload,
      {
        headers: this.authHeaders()
          .set('Content-Type', 'application/json')
          .set('Accept', 'application/json'),
      }
    ));
  }

  toMrfCustomizeFilterPayload(
    filters: MrfLocationFilters,
    primaryFilterBy: MrfCustomizeFilterField
  ): MrfCustomizeFilterPayload {
    return {
      filterBy: MRF_CUSTOMIZE_FILTER_BY_API[primaryFilterBy],
      location: filters.los ?? null,
      zone: filters.zoneName ?? null,
      subZone: filters.subZone ?? null,
      stateId: filters.stateId ?? null,
      cityId: filters.cityId ?? null,
      campusId: filters.campusId ?? null,
      deanName: filters.deanName ?? null,
    };
  }

  /** Load customize-filter dropdown options via CoustomizeFilters API. */
  getMrfCustomizeFilterOptions(
    filters: MrfLocationFilters,
    targetFilterBy: MrfCustomizeFilterField
  ): Observable<any[]> {
    const payload = this.toMrfCustomizeFilterPayload(filters, targetFilterBy);
    return this.postMrfCustomizeFilters(payload).pipe(
      map((res) => this.parseCustomizeFilterOptionsResponse(res, targetFilterBy))
    );
  }

  parseCustomizeFilterOptionsResponse(
    res: unknown,
    targetFilterBy: MrfCustomizeFilterField
  ): any[] {
    if (res == null) {
      return [];
    }
    switch (targetFilterBy) {
      case 'los':
        return this.parseLocationsResponse(res);
      case 'state':
        return this.parseStatesResponse(res);
      case 'city':
        return this.parseCitiesResponse(res);
      case 'zone':
        return this.parseZonesResponse(res);
      case 'subZone':
        return this.parseSubZonesResponse(res);
      case 'deanName':
        return this.parseDeansResponse(res);
      case 'campus':
        return this.parseCampusesResponse(res);
      default:
        return [];
    }
  }

  toTrackHistoryFilterPayload(
    filters?: MrfLocationFilters,
    period?: { month?: number | null; year?: number | null }
  ): MrfTrackHistoryFilterPayload {
    return {
      stateId: filters?.stateId ?? null,
      zone: filters?.zoneName ?? null,
      subZone: filters?.subZone ?? null,
      cityId: filters?.cityId ?? null,
      campusId: filters?.campusId ?? null,
      buildingId: filters?.buildingId ?? null,
      month: period?.month ?? null,
      year: period?.year ?? null,
      location: filters?.los ?? null,
      deanName: filters?.deanName ?? null,
    };
  }

  getEmployeeLos() {
    return this.mrfHttp(
      this.http.get(`${this.masterDomainUrl}/apiV2/getLocations`, {
        headers: this.authHeaders().set('Accept', '*/*'),
      })
    ).pipe(map((res) => this.parseLocationsResponse(res)));
  }

  parseLocationsResponse(
    res: unknown
  ): Array<Record<string, unknown> & { id: number | string | null; name: string; location: string }> {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['location'] ?? row['los'] ?? row['losName'] ?? '').trim();
        if (!name) {
          return null;
        }
        const idRaw = row['id'] ?? row['locationId'] ?? null;
        return {
          ...row,
          id: idRaw as number | string | null,
          name,
          location: name,
        };
      })
      .filter(
        (item): item is Record<string, unknown> & { id: number | string | null; name: string; location: string } =>
          !!item
      );
  }

  getLosStates(location: string) {
    const params = new HttpParams().set('location', String(location ?? '').trim());
    return this.mrfHttp(
      this.http.get(`${this.masterDomainUrl}/apiV2/getStatesByLocation`, {
        headers: this.authHeaders().set('Accept', '*/*'),
        params,
      })
    ).pipe(map((res) => this.parseStatesResponse(res)));
  }

  getEmpStates() {
    return this.mrfHttp(
      this.http.get(`${this.baseUrl}${COMMON_API}/employeeStates`, {
      headers: this.authHeaders(),
      })
    ).pipe(map((res) => this.parseStatesResponse(res)));
  }

  parseStatesResponse(
    res: unknown
  ): Array<
    Record<string, unknown> & {
      id: number | string;
      stateId: number | string;
      name: string;
      stateName: string;
    }
  > {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['stateName'] ?? '').trim();
        const idRaw = row['id'] ?? row['stateId'] ?? null;
        if (!name || idRaw == null || idRaw === '') {
          return null;
        }
        return {
          ...row,
          id: idRaw as number | string,
          stateId: idRaw as number | string,
          name,
          stateName: name,
        };
      })
      .filter(
        (
          item
        ): item is Record<string, unknown> & {
          id: number | string;
          stateId: number | string;
          name: string;
          stateName: string;
        } => !!item
      );
  }

  getStateCities(stateId: string | number) {
    return this.mrfHttp(
      this.http.get(`${this.baseUrl}${COMMON_API}/emplStateCities?stateId=${stateId}`, {
        headers: this.authHeaders(),
      })
    ).pipe(map((res) => this.parseCitiesResponse(res)));
  }

  parseCitiesResponse(
    res: unknown
  ): Array<
    Record<string, unknown> & {
      id: number | string;
      cityId: number | string;
      name: string;
      cityName: string;
    }
  > {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['cityName'] ?? '').trim();
        const idRaw = row['id'] ?? row['cityId'] ?? null;
        if (!name || idRaw == null || idRaw === '') {
          return null;
        }
        return {
          ...row,
          id: idRaw as number | string,
          cityId: idRaw as number | string,
          name,
          cityName: name,
        };
      })
      .filter(
        (
          item
        ): item is Record<string, unknown> & {
          id: number | string;
          cityId: number | string;
          name: string;
          cityName: string;
        } => !!item
      );
  }

  getStateZones(stateId: string | number) {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${COMMON_API}/userStateZones?stateId=${stateId}`, {
      headers: this.authHeaders(),
    }));
  }

  getCityZones(cityId: string | number) {
    const params = new HttpParams().set('cityId', String(cityId ?? '').trim());
    return this.mrfHttp(
      this.http.get(`${this.masterDomainUrl}/apiV2/getCityWiseZone`, {
        headers: this.authHeaders().set('Accept', '*/*'),
        params,
      })
    ).pipe(map((res) => this.parseCityWiseZonesResponse(res)));
  }

  parseCityWiseZonesResponse(res: unknown): Array<{ zoneName: string }> {
    return this.parseZonesResponse(res).map((item) => ({ zoneName: item.zoneName }));
  }

  parseZonesResponse(
    res: unknown
  ): Array<
    Record<string, unknown> & {
      id: number | string | null;
      name: string;
      zoneName: string;
      zone: string;
    }
  > {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['zoneName'] ?? row['zone'] ?? '').trim();
        if (!name) {
          return null;
        }
        const idRaw = row['id'] ?? row['zoneId'] ?? null;
        return {
          ...row,
          id: idRaw as number | string | null,
          name,
          zoneName: name,
          zone: name,
        };
      })
      .filter(
        (
          item
        ): item is Record<string, unknown> & {
          id: number | string | null;
          name: string;
          zoneName: string;
          zone: string;
        } => !!item
      );
  }

  getZoneSubZones(zone: string) {
    return this.mrfHttp(
      this.http.get(`${this.baseUrl}${COMMON_API}/userZoneSubZones?zone=${encodeURIComponent(zone)}`, {
        headers: this.authHeaders(),
      })
    ).pipe(map((res) => this.parseSubZonesResponse(res)));
  }

  parseSubZonesResponse(
    res: unknown
  ): Array<
    Record<string, unknown> & {
      id: number | string | null;
      name: string;
      subZone: string;
      subZoneName: string;
    }
  > {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['subZone'] ?? row['subZoneName'] ?? '').trim();
        if (!name) {
          return null;
        }
        const idRaw = row['id'] ?? row['subZoneId'] ?? null;
        return {
          ...row,
          id: idRaw as number | string | null,
          name,
          subZone: name,
          subZoneName: name,
        };
      })
      .filter(
        (
          item
        ): item is Record<string, unknown> & {
          id: number | string | null;
          name: string;
          subZone: string;
          subZoneName: string;
        } => !!item
      );
  }

  getSubZoneDeans(subZone: string) {
    const params = new HttpParams().set('subzone', String(subZone ?? '').trim());
    return this.mrfHttp(
      this.http.get(`${this.masterDomainUrl}/apiV2/getDeansBySubzone`, {
        headers: this.authHeaders().set('Accept', '*/*'),
        params,
      })
    ).pipe(map((res) => this.parseDeansResponse(res)));
  }

  parseDeansResponse(
    res: unknown
  ): Array<Record<string, unknown> & { id: number | string | null; name: string; deanName: string }> {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['deanName'] ?? row['principalDeanName'] ?? '').trim();
        if (!name) {
          return null;
        }
        const idRaw = row['id'] ?? row['deanId'] ?? null;
        return {
          ...row,
          id: idRaw as number | string | null,
          name,
          deanName: name,
        };
      })
      .filter(
        (item): item is Record<string, unknown> & { id: number | string | null; name: string; deanName: string } =>
          !!item
      );
  }

  getZoneCities(zone: string) {
    return this.mrfHttp(
      this.http.get(`${this.baseUrl}${COMMON_API}/userZoneCities?zone=${encodeURIComponent(zone)}`, {
        headers: this.authHeaders(),
      })
    ).pipe(map((res) => this.parseCitiesResponse(res)));
  }

  getDeanCampuses(deanName: string) {
    const params = new HttpParams().set('deanName', String(deanName ?? '').trim());
    return this.mrfHttp(
      this.http.get(`${this.masterDomainUrl}/apiV2/getCampusesByDeans`, {
        headers: this.authHeaders().set('Accept', '*/*'),
        params,
      })
    ).pipe(map((res) => this.parseCampusesResponse(res)));
  }

  getCityCampuses(cityId: string | number) {
    return this.mrfHttp(
      this.http.get(`${this.baseUrl}${COMMON_API}/empCityCampuses?cityId=${cityId}`, {
      headers: this.authHeaders(),
      })
    ).pipe(map((res) => this.parseCampusesResponse(res)));
  }

  parseCampusesResponse(
    res: unknown
  ): Array<
    Record<string, unknown> & {
      id: number | string;
      campusId: number | string;
      name: string;
      campusName: string;
    }
  > {
    const list = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data ?? [])
        : [];

    return list
      .map((item) => {
        const row =
          item && typeof item === 'object' && !Array.isArray(item)
            ? (item as Record<string, unknown>)
            : {};
        const name = String(row['name'] ?? row['campusName'] ?? row['campus'] ?? '').trim();
        const idRaw = row['id'] ?? row['campusId'] ?? null;
        if (!name || idRaw == null || idRaw === '') {
          return null;
        }
        return {
          ...row,
          id: idRaw as number | string,
          campusId: idRaw as number | string,
          name,
          campusName: name,
        };
      })
      .filter(
        (
          item
        ): item is Record<string, unknown> & {
          id: number | string;
          campusId: number | string;
          name: string;
          campusName: string;
        } => !!item
      );
  }

  getCampusBuildings(campusId: string | number) {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${COMMON_API}/empCmpBuilding?campusId=${campusId}`, {
      headers: this.authHeaders(),
    }));
  }

  private buildFilterQuery(filters?: MrfLocationFilters, firstParam?: string): string {
    const params: string[] = [];
    if (firstParam) params.push(firstParam);
    if (filters?.los) params.push(`los=${encodeURIComponent(filters.los)}`);
    if (filters?.stateId != null) params.push(`stateId=${filters.stateId}`);
    if (filters?.cityId != null) params.push(`cityId=${filters.cityId}`);
    if (filters?.zoneName) params.push(`zone=${encodeURIComponent(filters.zoneName)}`);
    if (filters?.subZone) params.push(`subZone=${encodeURIComponent(filters.subZone)}`);
    if (filters?.deanName) params.push(`deanName=${encodeURIComponent(filters.deanName)}`);
    if (filters?.campusId != null) params.push(`campusId=${filters.campusId}`);
    if (filters?.buildingId != null) params.push(`buildingId=${filters.buildingId}`);
    return params.length ? `?${params.join('&')}` : '';
  }

  saveMisReview(payload: any) {
    return this.mrfHttp(this.http.post(`${this.baseUrl}${MRF_API}/saveMisReview`, payload, {
      headers: this.authHeaders(),
    }));
  }

  saveHrReview(payload: any) {
    return this.mrfHttp(this.http.post(`${this.baseUrl}${MRF_API}/saveHrReview`, payload, {
      headers: this.authHeaders(),
    }));
  }

  saveEmployeeAssessment(payload: SaveEmployeeAssessmentPayload) {
    return this.mrfHttp(this.http.post(`${this.baseUrl}${MRF_API}/saveEmployeeAssessment`, payload, {
      headers: this.authHeaders()
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json'),
      responseType: 'text',
    }));
  }

  savePrincipalDeanReview(payload: {
    mrfFormId: number;
    remarks?: string;
    mrfFormStatus: 'APPROVED' | 'REJECTED';
  }) {
    return this.mrfHttp(this.http.post(`${this.baseUrl}${MRF_API}/savePrincipalDeanReview`, payload, {
      headers: this.authHeaders().set('Content-Type', 'application/json'),
    }));
  }

  saveMrfForm(payload: SaveMrfFormPayload) {
    return this.mrfHttp(this.http.post(`${this.baseUrl}${MRF_API}/saveMrfForm`, payload, {
      headers: this.authHeaders().set('Content-Type', 'application/json'),
    }));
  }

  /** POST /apiV2/saveEmployeeWithdraw — body: { payrollId, remarks } */
  saveEmployeeWithdrawal(payload: SaveEmployeeWithdrawalPayload): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/apiV2/saveEmployeeWithdraw`, payload, {
      headers: this.authHeaders()
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json'),
      responseType: 'text',
    });
  }

  mapEmployeeWithdrawalViewData(item: Record<string, unknown>): EmployeeWithdrawalView {
    const record = this.mapLeftEmpSearchToViewData(item);
    const pick = (...keys: string[]): string => {
      for (const key of keys) {
        const val = record[key];
        if (val != null && String(val).trim() !== '' && String(val) !== '0') {
          return String(val).trim();
        }
      }
      return '';
    };

    const payrollId = pick('payrollId', 'payRollId', 'payroll', 'replacementPayrollId');
    const employeeId = pick('employeeId', 'empId', 'replacementEmpId', 'uniqueId', 'id');

    return {
      payrollId: payrollId || employeeId,
      employeeId,
      employeeName: pick('empName', 'employeeName', 'userName', 'name', 'replacementEmpName'),
      designation: pick('desigName', 'designation', 'designationName', 'replacementDesigName'),
      department: pick('deptName', 'departmentName', 'department'),
      campusName: pick('campusName', 'campus', 'locationName', 'branchName'),
      mobileNo: pick('mobileNo', 'mobileNumber', 'mobile', 'phoneNo', 'contactNo'),
      email: pick('email', 'emailId', 'employeeEmail', 'officialEmail'),
      dateOfJoining: pick('doj', 'dateOfJoining', 'joiningDate', 'dateOfJoin'),
      employeeType: pick('empType', 'employeeType', 'employmentType', 'empTypeName'),
    };
  }

  emptyManpowerBudgetDashboardFilters(): ManpowerBudgetDashboardFilters {
    return {
      location: null,
      stateId: null,
      cityId: null,
      zone: null,
      subZone: null,
      campusId: null,
      deanName: null,
      empTypeId: null,
      deptId: null,
      desigId: null,
    };
  }

  /** POST /employee/api/jwtV2/mrf/manpowerBudgetDashboard — manpower budget report KPIs + details. */
  getManpowerBudgetDashboard(filters?: Partial<ManpowerBudgetDashboardFilters>): Observable<unknown> {
    const payload: ManpowerBudgetDashboardFilters = {
      ...this.emptyManpowerBudgetDashboardFilters(),
      ...filters,
    };
    return this.mrfHttp(
      this.http.post(`${this.masterDomainUrl}${MRF_API}/manpowerBudgetDashboard`, payload, {
        headers: this.authHeaders()
          .set('Content-Type', 'application/json')
          .set('Accept', 'application/json'),
      })
    );
  }

  parseManpowerBudgetDashboard(res: unknown): ManpowerBudgetDashboard {
    const root =
      res && typeof res === 'object' && !Array.isArray(res) ? (res as Record<string, unknown>) : {};
    const nested =
      root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
        ? (root['data'] as Record<string, unknown>)
        : root;
    const num = (key: string): number => {
      const value = nested[key];
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    const text = (row: Record<string, unknown>, ...keys: string[]): string => {
      for (const key of keys) {
        const value = row[key];
        if (value != null && String(value).trim()) {
          return String(value).trim();
        }
      }
      return '';
    };
    const rowNum = (row: Record<string, unknown>, ...keys: string[]): number => {
      for (const key of keys) {
        const value = row[key];
        const n = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(n)) {
          return n;
        }
      }
      return 0;
    };
    const rawDetails = Array.isArray(nested['details']) ? nested['details'] : [];
    const details: ManpowerBudgetDashboardDetail[] = rawDetails
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((row) => ({
        sno: rowNum(row, 'sno'),
        campusName: text(row, 'campusName'),
        stateName: text(row, 'stateName'),
        cityName: text(row, 'cityName'),
        zone: text(row, 'zone'),
        subZone: text(row, 'subZone'),
        deanName: text(row, 'deanName'),
        department: text(row, 'department'),
        designation: text(row, 'designation'),
        subjectName: text(row, 'subjectName', 'subName', 'subject'),
        empTypeName: text(row, 'empTypeName'),
        teachingType: text(row, 'empTypeName'),
        budgetedPositions: rowNum(row, 'budgetedPositions'),
        filledPositions: rowNum(row, 'filledPositions'),
        positionBalance: rowNum(row, 'positionBalance'),
        allocatedBudget: rowNum(row, 'allocatedBudget'),
        utilizedBudget: rowNum(row, 'utilizedBudget'),
        balanceBudget: rowNum(row, 'balanceBudget'),
        budgetStatus: text(row, 'budgetStatus'),
      }));

    return {
      totalBudgetedPositions: num('totalBudgetedPositions'),
      filledPositions: num('filledPositions'),
      positionBalance: num('positionBalance'),
      totalBudgetAmount: num('totalBudgetAmount'),
      utilizedBudget: num('utilizedBudget'),
      balanceBudget: num('balanceBudget'),
      positionWithinLimit: num('positionWithinLimit'),
      positionExceeded: num('positionExceeded'),
      budgetWithinLimit: num('budgetWithinLimit'),
      budgetExceeded: num('budgetExceeded'),
      positionPercentage: num('positionPercentage'),
      budgetPercentage: num('budgetPercentage'),
      details,
    };
  }

  /** POST /employee/api/jwtV2/mrf/getCampusBudgetReportWithFilters — budget report list. */
  getCampusBudgetReportWithFilters(
    filters?: MrfLocationFilters,
    period?: { month?: number | null; year?: number | null }
  ): Observable<unknown> {
    const payload = this.toTrackHistoryFilterPayload(filters, period);
    return this.mrfHttp(
      this.http.post(
        `${this.masterDomainUrl}/employee/api/jwtV2/mrf/getCampusBudgetReportWithFilters`,
        payload,
        {
          headers: this.authHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json'),
        }
      )
    );
  }

  /** @deprecated Use getCampusBudgetReportWithFilters. */
  getCampusBudgetWithFilters(filters?: MrfLocationFilters): Observable<unknown> {
    return this.getCampusBudgetReportWithFilters(filters);
  }

  parseBudgetReportResponse(res: unknown): BudgetReportResult {
    const root =
      res && typeof res === 'object' && !Array.isArray(res) ? (res as Record<string, unknown>) : {};
    const nested =
      root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
        ? (root['data'] as Record<string, unknown>)
        : root;

    const list = this.extractCampusBudgetReportList(res, root, nested);
    const flattened = this.flattenCampusBudgetReportDetails(list);

    const headerTotal = this.sumCampusBudgetTotals(list);
    const nestedTotal = this.toBudgetNumber(
      nested['totalBudget'] ??
        nested['totalBudgetAmount'] ??
        root['totalBudget'] ??
        root['totalBudgetAmount']
    );

    const rows = flattened
      .map((item) => this.mapBudgetReportRow(item))
      .filter((row): row is BudgetReportRow => !!row);

    const computedTotal = rows.reduce((sum, row) => {
      const amount = row.budgetAmount;
      return sum + (amount != null && Number.isFinite(amount) ? amount : 0);
    }, 0);

    return {
      rows,
      totalBudget: nestedTotal ?? headerTotal ?? (computedTotal || null),
    };
  }

  private extractCampusBudgetReportList(
    res: unknown,
    root: Record<string, unknown>,
    nested: Record<string, unknown>
  ): unknown[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (this.isCampusBudgetReportRecord(nested)) {
      return [nested];
    }
    if (this.isCampusBudgetReportRecord(root)) {
      return [root];
    }
    if (Array.isArray(root['data'])) {
      return root['data'] as unknown[];
    }
    if (Array.isArray(root['result'])) {
      return root['result'] as unknown[];
    }
    if (Array.isArray(root['response'])) {
      return root['response'] as unknown[];
    }
    if (Array.isArray(root['content'])) {
      return root['content'] as unknown[];
    }
    if (Array.isArray(root['budgetList'])) {
      return root['budgetList'] as unknown[];
    }
    if (Array.isArray(root['manpowerBudgetList'])) {
      return root['manpowerBudgetList'] as unknown[];
    }
    if (Array.isArray(nested['budgetList'])) {
      return nested['budgetList'] as unknown[];
    }
    if (Array.isArray(nested['rows'])) {
      return nested['rows'] as unknown[];
    }
    if (Array.isArray(nested['details'])) {
      return [nested];
    }
    return [];
  }

  private isCampusBudgetReportRecord(value: unknown): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }
    const rec = value as Record<string, unknown>;
    return Array.isArray(rec['details']) || rec['campusBudgetId'] != null;
  }

  private flattenCampusBudgetReportDetails(list: unknown[]): Record<string, unknown>[] {
    const flattened: Record<string, unknown>[] = [];
    for (const item of list) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        continue;
      }
      const campus = item as Record<string, unknown>;
      const details = campus['details'];
      if (!Array.isArray(details) || !details.length) {
        flattened.push(campus);
        continue;
      }
      const header: Record<string, unknown> = { ...campus };
      delete header['details'];
      for (const detail of details) {
        if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
          continue;
        }
        flattened.push({ ...header, ...(detail as Record<string, unknown>) });
      }
    }
    return flattened;
  }

  private sumCampusBudgetTotals(list: unknown[]): number | null {
    let total = 0;
    let found = false;
    for (const item of list) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        continue;
      }
      const amount = this.toBudgetNumber((item as Record<string, unknown>)['totalBudget']);
      if (amount == null) {
        continue;
      }
      found = true;
      total += amount;
    }
    return found ? total : null;
  }

  private toBudgetNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const num = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(num) ? num : null;
  }

  private mapBudgetReportRow(item: unknown): BudgetReportRow | null {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return null;
    }
    const row = item as Record<string, unknown>;
    const pickString = (...keys: string[]): string => {
      for (const key of keys) {
        const value = row[key];
        if (value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
      return '—';
    };
    const pickNum = (...keys: string[]): number | null => {
      for (const key of keys) {
        const value = row[key];
        if (value == null || value === '') {
          continue;
        }
        const num = Number(String(value).replace(/,/g, ''));
        if (Number.isFinite(num)) {
          return num;
        }
      }
      return null;
    };

    const campusId = pickNum('campusId', 'campusID', 'cmpId');
    const budgetAmount = pickNum('budgetAmount', 'budget', 'amount', 'salaryBudget');
    const teachTypeId = pickNum('teachTypeId', 'teachingTypeId', 'typeId');
    const empTypeId = pickNum('empTypeId', 'employeeTypeId');
    const subjectName = pickString('subjectName', 'subName', 'subject');
    const teachingTypeRaw = pickString(
      'roleType',
      'TeachType',
      'teachType',
      'teachTypeName'
    );
    const empTypeRaw = pickString('empType', 'employeeType', 'empTypeName', 'employmentType');
    const subjectId = pickNum('subjectId', 'subId', 'subject_id', 'SubjectId', 'SubId');
    const teachingType =
      teachingTypeRaw !== '—' ? teachingTypeRaw : this.formatBudgetReportTeachType(teachTypeId);
    const empType = empTypeRaw;

    return {
      budgetId: pickNum('campusBudgetId', 'budgetId', 'id'),
      city: pickString('cityName', 'city'),
      zone: pickString('zone', 'zoneName'),
      subZone: pickString('subZone', 'subZoneName', 'subzone'),
      campus: pickString('campusName', 'campus'),
      campusId,
      deanName: pickString('deanName', 'dean'),
      empType,
      empTypeId,
      teachingType,
      teachTypeId,
      department: pickString('department', 'deptName', 'departmentName'),
      designation: pickString('designation', 'desigName', 'designationName'),
      desigId: pickNum('desigId', 'designationId', 'desig_id'),
      subjectId,
      subjectName: subjectName !== '—' ? subjectName : '',
      budgetAmount,
      editable: this.parseBudgetReportEditableFlag(row['editable']),
      allottedEmpCount: pickNum(
        'desigEmpCount',
        'designationEmpCount',
        'allottedEmpCount',
        'empCount'
      ),
      raw: row,
    };
  }

  private parseBudgetReportEditableFlag(value: unknown): boolean | null {
    if (value === true || value === 1 || value === '1') {
      return true;
    }
    if (value === false || value === 0 || value === '0') {
      return false;
    }
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase();
    if (normalized === 'true' || normalized === 'y' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === 'n' || normalized === 'no') {
      return false;
    }
    return null;
  }

  /** Budget amount is editable only when no employees are allotted to that designation. */
  isCampusBudgetAmountEditable(row: BudgetReportRow | null | undefined): boolean {
    if (!row) {
      return true;
    }
    if (row.editable === false) {
      return false;
    }
    if (row.editable === true) {
      return true;
    }
    return !(row.allottedEmpCount != null && row.allottedEmpCount > 0);
  }

  applyBudgetReportTypeLabels(
    rows: BudgetReportRow[],
    empTypesRaw: unknown,
    teachTypesRaw: unknown
  ): BudgetReportRow[] {
    const empTypes = this.normalizeBudgetReportEmpTypes(empTypesRaw);
    const subjects = this.normalizeBudgetReportSubjects(empTypesRaw);
    const teachTypes = this.parseMrfTeachTypesResponse(teachTypesRaw);
    return rows.map((row) => {
      const empTypeId = row.empTypeId ?? this.toBudgetNumber(row.raw?.['empTypeId']);
      const teachTypeId = row.teachTypeId ?? this.toBudgetNumber(row.raw?.['teachTypeId']);
      const subjectId = row.subjectId ?? this.toBudgetNumber(row.raw?.['subjectId']);
      const empMatch = empTypes.find((item) => item.id != null && item.id === empTypeId);
      const teachMatch = teachTypes.find((item) => this.toBudgetNumber(item['typeId']) === teachTypeId);
      const subjectMatch = subjects.find((item) => item.id != null && item.id === subjectId);
      const empType = empMatch?.label || (row.empType !== '—' ? row.empType : '');
      const teachingType =
        this.getMrfTeachTypeLabel(teachMatch) ||
        (row.teachingType !== '—' ? row.teachingType : '');
      const subjectName =
        (row.subjectName && row.subjectName !== '—' ? row.subjectName : '') ||
        subjectMatch?.label ||
        '';
      return {
        ...row,
        empTypeId,
        teachTypeId,
        subjectId,
        empType: empType || '—',
        teachingType: teachingType || '—',
        subjectName,
      };
    });
  }

  private normalizeBudgetReportEmpTypes(
    raw: unknown
  ): Array<{ id: number | null; label: string }> {
    const root =
      raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    const data = (root['data'] && typeof root['data'] === 'object' ? root['data'] : root) as Record<
      string,
      unknown
    >;
    const list = data['employeeTypes'] ?? data['empTypes'] ?? root['employeeTypes'] ?? root['empTypes'];
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item) => {
        const row = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        const id = this.toBudgetNumber(row['empTypeId'] ?? row['employeeTypeId'] ?? row['id']);
        const label = String(row['empType'] ?? row['employeeType'] ?? row['empTypeName'] ?? row['name'] ?? '')
          .trim();
        return { id, label };
      })
      .filter((item) => !!item.label);
  }

  private normalizeBudgetReportSubjects(
    raw: unknown
  ): Array<{ id: number | null; label: string }> {
    const root =
      raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    const data = (root['data'] && typeof root['data'] === 'object' ? root['data'] : root) as Record<
      string,
      unknown
    >;
    const list = data['subjects'] ?? root['subjects'];
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item) => {
        const row = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        const id = this.toBudgetNumber(row['subId'] ?? row['subjectId'] ?? row['id']);
        const label = String(row['subName'] ?? row['subjectName'] ?? row['subject'] ?? row['name'] ?? '')
          .trim();
        return { id, label };
      })
      .filter((item) => !!item.label);
  }

  private formatBudgetReportTeachType(teachTypeId: number | null): string {
    if (teachTypeId === 1) {
      return 'TEACH';
    }
    if (teachTypeId === 2) {
      return 'NON TEACH';
    }
    return '—';
  }

  /** POST /employee/api/jwtV2/mrf/saveCampusBudget — returns response body (string or JSON). */
  saveCampusBudget(payload: SaveCampusBudgetPayload): Observable<unknown> {
    return this.http
      .post(`${this.baseUrl}${MRF_API}/saveCampusBudget`, payload, {
        headers: this.authHeaders()
          .set('Content-Type', 'application/json')
          .set('Accept', '*/*'),
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((res) => {
          const status = res.status;
          if (status < 200 || status >= 300) {
            throw res;
          }
          const raw = String(res.body ?? '').trim();
          if (!raw) {
            return { status, message: '' };
          }
          try {
            return JSON.parse(raw);
          } catch {
            // Plain-text success message from API
            return raw.replace(/^"|"$/g, '');
          }
        }),
        catchError((err) => this.catchMrfNoContent(err, null))
      );
  }

  /** Campuses allocated to the logged-in employee. */
  getEmpCampuses(): Observable<unknown> {
    return this.mrfHttp(this.http.get(`${this.baseUrl}${COMMON_API}/empCampuses`, {
      headers: this.authHeaders(),
    }));
  }

  /** Verifies qualified-employee Aadhaar (duplicate / eligibility check). */
  verifyQualifiedEmployeeAadhaar(aadhaarNo: string): Observable<MrfAadhaarVerifyView> {
    const digits = normalizeMrfAadhaarDigits(aadhaarNo);
    const validationError = validateMrfAadhaarNo(digits, 'Aadhaar No.');
    if (validationError) {
      // Regex + Verhoeff failed — do not call verification API.
      if (digits.length !== MRF_AADHAAR_DIGIT_LENGTH) {
        return of({ alreadyExists: false, blocked: false, message: '' });
      }
      return of({
        alreadyExists: false,
        blocked: true,
        message: validationError,
      });
    }

    const url =
      `${this.baseUrl}${MRF_API}/checkEmployeeByAadhaar?aadhaarNo=${encodeURIComponent(digits)}`;

    return this.mrfHttp(
      this.http.get(url, { headers: this.authHeaders().set('Accept', '*/*') })
    ).pipe(
      map((res) => this.parseMrfAadhaarVerifyResponse(res)),
      catchError((err) => {
        console.error('verifyAadhaar failed', err);
        const body =
          err && typeof err === 'object'
            ? (err as { error?: unknown }).error ?? err
            : err;
        const parsed = this.parseMrfAadhaarVerifyResponse(body);
        if (parsed.message || parsed.blocked || parsed.alreadyExists) {
          return of(parsed);
        }
        return of({
          alreadyExists: false,
          blocked: true,
          message: 'Unable to verify Aadhaar. Please try again.',
        });
      })
    );
  }

  private parseMrfAadhaarVerifyResponse(res: unknown): MrfAadhaarVerifyView {
    if (res == null) {
      return { alreadyExists: false, blocked: false, message: '' };
    }
    if (typeof res === 'boolean') {
      return {
        alreadyExists: res,
        blocked: false,
        message: res ? 'Aadhaar number already exists.' : '',
      };
    }
    if (typeof res === 'string') {
      const text = res.trim();
      const exists = /already\s*exist/i.test(text);
      const blocked = !exists && !!text && /not eligible|must be completed|not found/i.test(text);
      return {
        alreadyExists: exists,
        blocked,
        message: exists || blocked ? text : '',
      };
    }
    if (typeof res !== 'object') {
      return { alreadyExists: false, blocked: false, message: '' };
    }

    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
    const obj =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : root;

    const message = String(
      obj['message'] ??
        obj['msg'] ??
        obj['statusMessage'] ??
        obj['errorMessage'] ??
        obj['error'] ??
        root['message'] ??
        ''
    ).trim();

    const status = String(obj['status'] ?? obj['STATUS'] ?? root['status'] ?? '')
      .trim()
      .toUpperCase();
    const successStatuses = new Set(['', 'OK', 'SUCCESS', 'FOUND', 'ACTIVE', 'ELIGIBLE']);
    const blockedByStatus = !successStatuses.has(status) && !!message;

    const exists =
      this.readTruthyFlag(
        obj,
        'alreadyExist',
        'alreadyExisted',
        'alreadyExists',
        'exists',
        'isExist',
        'isExists',
        'duplicate',
        'isDuplicate',
        'aadhaarExists',
        'aadharExists'
      ) ||
      /already\s*exist/i.test(message) ||
      (status.includes('EXIST') && !blockedByStatus);

    const employeeName = String(obj['employeeName'] ?? obj['name'] ?? '').trim();
    const prevChaitanyaId = String(
      obj['prevChaitanyaId'] ??
        obj['previousChaitanyaId'] ??
        obj['previousEmployeeId'] ??
        obj['prevEmpId'] ??
        ''
    ).trim();
    const tempPayrollId = String(obj['tempPayrollId'] ?? '').trim();
    const hasEmployeeData = !!(
      employeeName ||
      tempPayrollId ||
      obj['mobileNo'] ||
      prevChaitanyaId
    );

    const employeeData = hasEmployeeData
      ? this.mapTempEmpToViewData(obj)
      : undefined;

    const alreadyExists = exists && !hasEmployeeData;
    const blocked = blockedByStatus && !hasEmployeeData;

    return {
      alreadyExists,
      blocked,
      message:
        message ||
        (alreadyExists
          ? 'Aadhaar number already exists.'
          : blocked
            ? 'Employee is not eligible to apply.'
            : ''),
      employeeData,
      tempPayrollId,
    };
  }

  private readTruthyFlag(obj: Record<string, unknown>, ...keys: string[]): boolean {
    for (const key of keys) {
      const val = obj[key];
      if (val === true || val === 'true' || val === 'Y' || val === 1 || val === '1') {
        return true;
      }
      if (val === false || val === 'false' || val === 'N' || val === 0 || val === '0') {
        return false;
      }
    }
    return false;
  }

  /** GET .../getDocumentsByMrfFormId — returns JSON array of document metadata. */
  getMrfDocumentsByMrfFormId(mrfFormId: string | number): Observable<MrfDocumentView[]> {
    return this.mrfHttp(
      this.http.get<unknown>(
      `${this.baseUrl}${MRF_API}/getDocumentsByMrfFormId?mrfFormId=${encodeURIComponent(String(mrfFormId))}`,
        { headers: this.authHeaders().set('Accept', '*/*') }
      )
    ).pipe(
      map((res) => this.normalizeMrfDocumentList(res, mrfFormId)),
      catchError((err) => this.catchMrfNoContent(err, [] as MrfDocumentView[]))
    );
  }

  /** GET .../downloadDocument?mrfDocId= — download file bytes (requires auth). */
  downloadMrfDocument(mrfDocId: string | number): Observable<Blob> {
    return this.http.get(this.buildMrfDownloadDocumentUrl(mrfDocId), {
      headers: this.authHeaders(),
      responseType: 'blob',
    });
  }

  buildMrfDownloadDocumentUrl(mrfDocId: string | number): string {
    return `${this.baseUrl}${MRF_API}/downloadDocument?mrfDocId=${encodeURIComponent(String(mrfDocId))}`;
  }

  extractMrfDocumentsFromPayload(res: unknown): MrfDocumentView[] {
    if (!res || typeof res !== 'object') return [];
    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
    return this.normalizeMrfDocumentList(data, String(root['mrfFormId'] ?? ''));
  }

  getMrfDocumentViewUrl(doc: MrfDocumentView | Record<string, unknown>): string {
    const view = doc as MrfDocumentView;
    const record = doc as Record<string, unknown>;
    const direct = this.sanitizeMrfDocumentUrl(
      String(
        view.blobUrl ??
          view.fileUrl ??
          record['url'] ??
          record['documentUrl'] ??
          record['fileUrl'] ??
          record['path'] ??
          ''
      )
    );
    if (direct) return direct;

    const mrfDocId = view.mrfDocId ?? view.documentId ?? record['mrfDocId'] ?? record['documentId'];
    if (mrfDocId != null && String(mrfDocId).trim() !== '') {
      return this.buildMrfDownloadDocumentUrl(mrfDocId as string | number);
    }
    return '';
  }

  getMrfDocumentViewName(doc: MrfDocumentView | Record<string, unknown>): string {
    const record = doc as Record<string, unknown>;
    const direct = String(
      record['fileName'] ??
        record['documentName'] ??
        record['name'] ??
        record['originalFileName'] ??
        record['uploadedFileName'] ??
        record['documentFileName'] ??
        ''
    ).trim();
    if (direct) {
      return direct;
    }

    const filePath = String(record['filePath'] ?? record['path'] ?? '').trim();
    if (filePath) {
      const parts = filePath.split(/[/\\]/);
      const leaf = parts[parts.length - 1]?.trim();
      if (leaf) {
        return leaf;
      }
    }

    return 'Document';
  }

  mergeMrfDocumentLists(...lists: MrfDocumentView[][]): MrfDocumentView[] {
    const merged: MrfDocumentView[] = [];
    const seen = new Set<string>();

    for (const list of lists) {
      for (const doc of list ?? []) {
        if (!doc) continue;
        const key = String(
          doc.mrfDocId ??
            doc.documentId ??
            `${doc.fileName}|${doc.documentType}|${doc.mimeType}`
        );
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        merged.push(doc);
      }
    }

    return merged;
  }

  private normalizeMrfDocumentList(res: unknown, mrfFormId: string | number): MrfDocumentView[] {
    const rawList = this.extractMrfDocumentArray(res);
    return rawList.map((item) => this.mapMrfDocumentRecord(item, mrfFormId)).filter(Boolean) as MrfDocumentView[];
  }

  private extractMrfDocumentArray(res: unknown): Record<string, unknown>[] {
    if (Array.isArray(res)) {
      return res.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
    }
    if (!res || typeof res !== 'object') return [];
    const root = res as Record<string, unknown>;
    const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
    if (Array.isArray(data)) {
      return data.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
    }
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const nested =
        obj['documents'] ??
        obj['documentList'] ??
        obj['files'] ??
        obj['mrfDocuments'] ??
        obj['mrfDocumentList'] ??
        obj['uploadedDocuments'] ??
        obj['documentDetails'];
      if (Array.isArray(nested)) {
        return nested.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
      }
    }
    const nested =
      root['documents'] ??
      root['documentList'] ??
      root['files'] ??
      root['mrfDocuments'] ??
      root['mrfDocumentList'] ??
      root['uploadedDocuments'] ??
      root['documentDetails'];
    if (Array.isArray(nested)) {
      return nested.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
    }
    return [];
  }

  private mapMrfDocumentRecord(raw: Record<string, unknown>, _mrfFormId: string | number): MrfDocumentView | null {
    const fileName = this.getMrfDocumentViewName(raw).trim();
    const mrfDocId =
      raw['mrfDocId'] ??
      raw['mrfDocumentId'] ??
      raw['documentId'] ??
      raw['docId'] ??
      raw['id'] ??
      raw['fileId'];
    const mimeType = String(
      raw['fileType'] ?? raw['mimeType'] ?? raw['contentType'] ?? raw['fileMimeType'] ?? ''
    ).trim();
    const documentType = this.resolveMrfDocumentTypeFromRaw(raw, fileName, mimeType);

    if ((!fileName || fileName === 'Document') && mrfDocId == null) {
      return null;
    }

    const fileUrl =
      this.sanitizeMrfDocumentUrl(
        String(raw['url'] ?? raw['fileUrl'] ?? raw['documentUrl'] ?? raw['path'] ?? '')
      ) ||
      (mrfDocId != null && String(mrfDocId).trim() !== ''
        ? this.buildMrfDownloadDocumentUrl(mrfDocId as string | number)
        : '');

    return {
      fileName: fileName === 'Document' && mrfDocId != null ? `Document-${mrfDocId}` : fileName || 'Document',
      documentName: String(
        raw['documentName'] ?? raw['name'] ?? raw['originalFileName'] ?? fileName ?? 'Document'
      ),
      documentType,
      mimeType,
      fileUrl,
      documentId: mrfDocId as string | number | undefined,
      mrfDocId: mrfDocId as string | number | undefined,
    };
  }

  private sanitizeMrfDocumentUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (
      trimmed.includes('%22%7D') ||
      trimmed.includes('"%7D') ||
      trimmed.includes('","mrfDocId"') ||
      trimmed.includes('"mrfDocId"')
    ) {
      return '';
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return '';
  }

  private inferDocumentType(fileName: string, mimeType: string): string {
    const label = fileName.toLowerCase();
    const mime = mimeType.toLowerCase();
    if (label.includes('exam')) return MRF_DOCUMENT_TYPE.EXAM_COPY;
    if (label.includes('pan')) return MRF_DOCUMENT_TYPE.PAN;
    if (label.includes('aadhaar') || label.includes('aadhar')) return MRF_DOCUMENT_TYPE.AADHAR;
    if (label.includes('demo') || label.includes('video')) return 'demovideo';
    if (label.includes('job') || label.includes('work')) return MRF_DOCUMENT_TYPE.WORK_DESCRIPTION;
    if (label.includes('approval') || label.includes('approve')) {
      return MRF_DOCUMENT_TYPE.APPROVED_BY_MANAGEMENT;
    }
    if (mime.includes('video')) return 'demovideo';
    return '';
  }

  private resolveMrfDocumentTypeFromRaw(
    raw: Record<string, unknown>,
    fileName: string,
    mimeType: string
  ): string {
    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (value != null && String(value).trim() !== '' && String(value) !== '0') {
          return String(value).trim();
        }
      }
      return '';
    };

    const direct = pickString(
      raw['documentType'],
      raw['documentTypeName'],
      raw['documentCategory'],
      raw['category'],
      raw['categoryName'],
      raw['docCategory'],
      raw['type'],
      raw['docType'],
      raw['fileDocumentType'],
      raw['document_type']
    );
    if (direct) {
      return this.canonicalizeMrfDocumentType(direct);
    }

    const nestedType = raw['documentTypeMaster'] ?? raw['docTypeMaster'];
    if (nestedType && typeof nestedType === 'object') {
      const nested = nestedType as Record<string, unknown>;
      const nestedLabel = pickString(
        nested['documentType'],
        nested['documentTypeName'],
        nested['typeName'],
        nested['name']
      );
      if (nestedLabel) {
        return this.canonicalizeMrfDocumentType(nestedLabel);
      }
    }

    const typeIdRaw = raw['documentTypeId'] ?? raw['typeId'] ?? raw['docTypeId'];
    if (typeIdRaw != null && typeIdRaw !== '') {
      const byId = this.mapMrfDocumentTypeId(typeIdRaw);
      if (byId) {
        return byId;
      }
    }

    return this.inferDocumentType(fileName, mimeType);
  }

  private mapMrfDocumentTypeId(value: unknown): string {
    const id = Number(value);
    if (!Number.isNaN(id)) {
      const idMap: Record<number, string> = {
        1: MRF_DOCUMENT_TYPE.AADHAR,
        2: MRF_DOCUMENT_TYPE.PAN,
        3: MRF_DOCUMENT_TYPE.EXAM_COPY,
        4: MRF_DOCUMENT_TYPE.WORK_DESCRIPTION,
        5: MRF_DOCUMENT_TYPE.APPROVED_BY_MANAGEMENT,
      };
      if (idMap[id]) {
        return idMap[id];
      }
    }
    return '';
  }

  private resolveMrfDocumentBucket(doc: MrfDocumentView): keyof MrfDocumentBuckets | null {
    if (this.matchesMrfDocumentType(doc, MRF_DOCUMENT_TYPE.APPROVED_BY_MANAGEMENT)) {
      return 'approvedByManagement';
    }
    if (this.matchesMrfDocumentType(doc, MRF_DOCUMENT_TYPE.WORK_DESCRIPTION)) {
      return 'workDescription';
    }
    if (this.isMrfExamDocument(doc)) {
      return 'exam';
    }
    if (this.isMrfDemoVideoDocument(doc)) {
      return 'demoVideo';
    }
    if (this.matchesMrfDocumentType(doc, MRF_DOCUMENT_TYPE.PAN)) {
      return 'pan';
    }
    if (this.matchesMrfDocumentType(doc, MRF_DOCUMENT_TYPE.AADHAR)) {
      return 'aadhaar';
    }
    return null;
  }

  private resolveMrfDocumentFallbackBucket(doc: MrfDocumentView): keyof MrfDocumentBuckets | null {
    const label = this.getMrfDocumentTypeLabel(doc).toLowerCase();
    const name = this.getMrfDocumentViewName(doc).toLowerCase();

    if (label.includes('pan') || name.includes('pan')) return 'pan';
    if (label.includes('aadhar') || label.includes('aadhaar') || name.includes('aadhar')) {
      return 'aadhaar';
    }
    if (label.includes('exam') || name.includes('exam')) return 'exam';
    if (this.isMrfDemoVideoDocument(doc)) return 'demoVideo';
    if (label.includes('work') || label.includes('job') || name.includes('work') || name.includes('job')) {
      return 'workDescription';
    }
    if (label.includes('approval') || label.includes('approve') || name.includes('approval')) {
      return 'approvedByManagement';
    }

    return null;
  }

  private reassignAttachedEmployeeDocuments(
    buckets: MrfDocumentBuckets,
    options?: { examTaken?: boolean }
  ): void {
    const examTaken = options?.examTaken !== false;
    const stillAttached: MrfDocumentView[] = [];
    let hasPan = buckets.pan.length > 0;
    let hasExam = buckets.exam.length > 0;
    let hasAadhaar = buckets.aadhaar.length > 0;

    for (const doc of buckets.attached) {
      let target: keyof MrfDocumentBuckets | null = null;

      if (this.isMrfDemoVideoDocument(doc)) {
        target = 'demoVideo';
      } else if (this.isMrfPdfDocument(doc) && examTaken && !hasExam) {
        target = 'exam';
        hasExam = true;
      } else if (this.isMrfImageDocument(doc) && !hasPan) {
        target = 'pan';
        hasPan = true;
      } else if (this.isMrfImageDocument(doc) && !hasAadhaar) {
        target = 'aadhaar';
        hasAadhaar = true;
      } else if (this.isMrfPdfDocument(doc) && !buckets.workDescription.length) {
        target = 'workDescription';
      }

      if (target) {
        buckets[target].push(doc);
      } else {
        stillAttached.push(doc);
      }
    }

    buckets.attached = stillAttached;
  }

  normalizeMrfDocumentTypeToken(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
  }

  private canonicalizeMrfDocumentType(value: string): string {
    const token = this.normalizeMrfDocumentTypeToken(value);
    if (!token) return value.trim();

    if (token === 'aadhar' || token === 'aadhaar') {
      return MRF_DOCUMENT_TYPE.AADHAR;
    }
    if (token === 'pan') {
      return MRF_DOCUMENT_TYPE.PAN;
    }
    if (token.includes('exam')) {
      return MRF_DOCUMENT_TYPE.EXAM_COPY;
    }
    if (token.includes('workdescription') || token.includes('jobdescription')) {
      return MRF_DOCUMENT_TYPE.WORK_DESCRIPTION;
    }
    if (token.includes('approvedbymanagement') || token.includes('approvedby')) {
      return MRF_DOCUMENT_TYPE.APPROVED_BY_MANAGEMENT;
    }
    if (token.includes('demo') || token.includes('video')) {
      return 'demovideo';
    }

    return value.trim();
  }

  getMrfDocumentTypeLabel(doc: MrfDocumentView | Record<string, unknown>): string {
    const record = doc as Record<string, unknown>;
    return String(
      (doc as MrfDocumentView).documentType ??
        record['documentType'] ??
        record['documentTypeName'] ??
        record['type'] ??
        record['docType'] ??
        ''
    ).trim();
  }

  matchesMrfDocumentType(
    doc: MrfDocumentView | Record<string, unknown>,
    documentType: string
  ): boolean {
    const expected = this.normalizeMrfDocumentTypeToken(documentType);
    if (!expected) return false;

    const actual = this.normalizeMrfDocumentTypeToken(this.getMrfDocumentTypeLabel(doc));
    if (actual === expected) return true;

    if (expected === 'aadhar') {
      return actual === 'aadhar' || actual === 'aadhaar' || actual.includes('aadhar');
    }
    if (expected === 'pan') {
      return actual === 'pan' || actual.includes('pan');
    }
    if (expected === 'workdescription') {
      return (
        actual.includes('workdescription') ||
        actual.includes('jobdescription') ||
        actual.includes('workdesc')
      );
    }
    if (expected === 'approvedbymanagement') {
      return (
        actual.includes('approvedbymanagement') ||
        actual.includes('approvedby') ||
        actual.includes('approval')
      );
    }
    if (expected === 'exam') {
      return actual.includes('exam') || actual.includes('examcopy');
    }
    if (expected === 'demovideo') {
      return actual.includes('demo') || actual.includes('video');
    }

    return actual.includes(expected) || expected.includes(actual);
  }

  isMrfExamDocument(doc: MrfDocumentView | Record<string, unknown>): boolean {
    const label = `${this.getMrfDocumentTypeLabel(doc)} ${this.getMrfDocumentViewName(doc)}`.toLowerCase();
    return label.includes('exam');
  }

  isMrfDemoVideoDocument(doc: MrfDocumentView | Record<string, unknown>): boolean {
    const label = `${this.getMrfDocumentTypeLabel(doc)} ${this.getMrfDocumentViewName(doc)}`.toLowerCase();
    const mimeType = String((doc as MrfDocumentView).mimeType ?? '').toLowerCase();
    return label.includes('demo') || label.includes('video') || mimeType.includes('video');
  }

  isMrfPdfDocument(doc: MrfDocumentView | Record<string, unknown>): boolean {
    const name = this.getMrfDocumentViewName(doc).toLowerCase();
    const mimeType = String((doc as MrfDocumentView).mimeType ?? '').toLowerCase();
    return name.endsWith('.pdf') || mimeType.includes('pdf');
  }

  isMrfImageDocument(doc: MrfDocumentView | Record<string, unknown>): boolean {
    const name = this.getMrfDocumentViewName(doc).toLowerCase();
    const mimeType = String((doc as MrfDocumentView).mimeType ?? '').toLowerCase();
    return (
      /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/.test(name) ||
      mimeType.startsWith('image/')
    );
  }

  isMrfPdfOrImageFile(file: File): boolean {
    const name = file.name.toLowerCase();
    const mimeType = String(file.type ?? '').toLowerCase();
    return (
      name.endsWith('.pdf') ||
      mimeType.includes('pdf') ||
      mimeType.startsWith('image/') ||
      /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/.test(name)
    );
  }

  getMrfDocumentIconLabel(doc: MrfDocumentView | Record<string, unknown>): string {
    if (this.isMrfDemoVideoDocument(doc)) return '▶';
    if (this.isMrfPdfDocument(doc)) return 'PDF';
    if (this.isMrfImageDocument(doc)) return 'IMG';
    return 'FILE';
  }

  canDownloadMrfDocument(doc: MrfDocumentView | Record<string, unknown>): boolean {
    const view = doc as MrfDocumentView;
    const record = doc as Record<string, unknown>;
    if (view.blobUrl || view.fileUrl) return true;
    const mrfDocId =
      view.mrfDocId ??
      view.documentId ??
      record['mrfDocId'] ??
      record['mrfDocumentId'] ??
      record['documentId'];
    return mrfDocId != null && String(mrfDocId).trim() !== '' && String(mrfDocId) !== '0';
  }

  triggerMrfDocumentDownload(doc: MrfDocumentView | Record<string, unknown>): Observable<void> {
    const view = doc as MrfDocumentView;
    const fileName = this.getMrfDocumentViewName(doc);

    if (view.blobUrl) {
      this.openAndDownloadMrfDocument(view.blobUrl, fileName, doc, true);
      return of(undefined);
    }

    const directUrl = this.getMrfDocumentViewUrl(doc);
    if (directUrl.startsWith('http://') || directUrl.startsWith('https://')) {
      this.openAndDownloadMrfDocument(directUrl, fileName, doc, true);
      return of(undefined);
    }

    const record = doc as Record<string, unknown>;
    const mrfDocId = view.mrfDocId ?? view.documentId ?? record['mrfDocId'] ?? record['documentId'];
    if (mrfDocId == null || String(mrfDocId).trim() === '') {
      return of(undefined);
    }

    return this.downloadMrfDocument(mrfDocId as string | number).pipe(
      map((blob) => {
        const mimeType = String(view.mimeType ?? record['mimeType'] ?? record['fileType'] ?? '').trim();
        const typedBlob =
          blob.type || !mimeType ? blob : new Blob([blob], { type: mimeType });
        this.openAndDownloadMrfDocument(typedBlob, fileName, doc);
        return undefined;
      })
    );
  }

  shouldPreviewMrfDocumentInNewTab(doc: MrfDocumentView | Record<string, unknown>): boolean {
    return this.isMrfPdfDocument(doc) || this.isMrfImageDocument(doc);
  }

  private openAndDownloadMrfDocument(
    source: Blob | string,
    fileName: string,
    doc: MrfDocumentView | Record<string, unknown>,
    isExistingObjectUrl = false
  ): void {
    const url = isExistingObjectUrl ? String(source) : URL.createObjectURL(source as Blob);

    if (this.shouldPreviewMrfDocumentInNewTab(doc)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || 'document';
    anchor.rel = 'noopener';
    anchor.click();

    if (!isExistingObjectUrl) {
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  }

  partitionMrfDocuments(
    docs: MrfDocumentView[],
    options?: { examTaken?: boolean }
  ): MrfDocumentBuckets {
    const buckets: MrfDocumentBuckets = {
      aadhaar: [],
      pan: [],
      exam: [],
      demoVideo: [],
      workDescription: [],
      approvedByManagement: [],
      attached: [],
    };
    const uncategorized: MrfDocumentView[] = [];

    for (const doc of docs) {
      const bucket = this.resolveMrfDocumentBucket(doc);
      if (bucket) {
        buckets[bucket].push(doc);
      } else {
        uncategorized.push(doc);
      }
    }

    for (const doc of uncategorized) {
      const bucket = this.resolveMrfDocumentFallbackBucket(doc);
      if (bucket) {
        buckets[bucket].push(doc);
      } else {
        buckets.attached.push(doc);
      }
    }

    this.reassignAttachedEmployeeDocuments(buckets, options);
    return buckets;
  }

  uploadMrfDocuments(mrfFormId: string | number, files: File[], documentType?: string) {
    const sizeMessage = getMrfUploadFileSizeValidationMessage(files);
    if (sizeMessage) {
      return throwError(() => new Error(sizeMessage));
    }

    const formData = new FormData();
    formData.append('mrfFormId', String(mrfFormId));
    if (documentType) {
      formData.append('documentType', documentType);
      formData.append('type', documentType);
    }
    files.forEach((file) => formData.append('files', file));
    return this.mrfHttp(this.http.post(`${this.baseUrl}${MRF_API}/uploadDocuments`, formData, {
      headers: this.authHeaders(),
      responseType: 'text',
    }));
  }

  /** Returns completed age in years from DOB, or null when DOB is invalid. */
  calculateEmployeeAge(dob: unknown, asOf: Date = new Date()): number | null {
    const birth = this.parseDobDate(dob);
    if (!birth) return null;

    let age = asOf.getFullYear() - birth.getFullYear();
    const monthDiff = asOf.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  }

  private parseDobDate(dob: unknown): Date | null {
    if (dob == null) return null;
    const raw = String(dob).trim();
    if (!raw) return null;

    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const dmy = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (dmy) {
      let year = Number(dmy[3]);
      if (dmy[3].length === 2) year = 2000 + year;
      const date = new Date(year, Number(dmy[2]) - 1, Number(dmy[1]));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}

@Injectable()
export class HrAlertService {
  private pendingDashboardAlert: { message: string; status: HrAlertStatus } | null = null;

  constructor(private dialog: MatDialog) {}

  /** Queue an alert to show on MRF dashboard after navigation. */
  showOnDashboard(message: string, status: HrAlertStatus = 'Success'): void {
    this.pendingDashboardAlert = { message, status };
  }

  consumePendingDashboardAlert(): { message: string; status: HrAlertStatus } | null {
    const alert = this.pendingDashboardAlert;
    this.pendingDashboardAlert = null;
    return alert;
  }

  show(message: string, status: HrAlertStatus = 'Success', autoCloseMs = 4000): void {
    const dialogRef = this.dialog.open(AlertComponent, {
      width: '400px',
      maxWidth: 'calc(100vw - 24px)',
      minHeight: '60px',
      position: { top: '10px', right: '10px' },
      hasBackdrop: false,
      panelClass: 'hr-alert-dialog-panel',
      autoFocus: false,
      data: { data: message, status },
    });

    if (autoCloseMs > 0) {
      setTimeout(() => dialogRef.close(), autoCloseMs);
    }
  }

  confirm(message: string, options?: Partial<MrfConfirmAlertData>): Observable<boolean> {
    return this.dialog
      .open(MrfConfirmAlertComponent, {
        width: '420px',
        disableClose: true,
        panelClass: 'mrf-confirm-alert-panel',
        data: {
          title: options?.title ?? 'Warning',
          message,
          confirmLabel: options?.confirmLabel ?? 'Confirm',
          cancelLabel: options?.cancelLabel ?? 'No',
        } satisfies MrfConfirmAlertData,
      })
      .afterClosed()
      .pipe(map((result) => !!result));
  }
}
